import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type DragEvent,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Link, Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowDown,
  ArrowDownToLine,
  ArrowLeft,
  ArrowUp,
  ArrowUpToLine,
  BookMarked,
  BookOpen,
  ChevronDown,
  ChevronRight,
  ClipboardList,
  FileText,
  FlaskConical,
  FolderOpen,
  FolderKanban,
  Check,
  CheckCircle2,
  Circle,
  CircleHelp,
  ChevronsDownUp,
  ChevronsUpDown,
  GraduationCap,
  GripVertical,
  Headphones,
  Eye,
  Image as ImageIcon,
  LayoutTemplate,
  Link2,
  ListChecks,
  Loader2,
  Lock,
  Unlock,
  MessageSquare,
  MoreVertical,
  Pencil,
  Plus,
  Presentation,
  Settings2,
  Trash2,
  TriangleAlert,
  Type,
  Upload,
  Video,
  Megaphone,
  Paperclip,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useNotification } from "@/contexts/NotificationContext";
import { Dialog, DIALOG_EXIT_MS, NotificationModal } from "@/components/modals";
import { afterPaint } from "@/components/notices/afterPaint";
import LoadingOverlay from "@/components/overlays/LoadingOverlay";
import {
  COURSE_DIALOG_STYLE,
  CourseMetaFields,
  type TaxonomyOption,
} from "@/components/courses/CourseMetaFields";
import ContainerStandardsField from "@/components/courses/ContainerStandardsField";
import ActivityCategoryFields, { activityMinuteTypesInvalid } from "@/components/courses/ActivityCategoryFields";
import RichTextEditor from "@/components/common/RichTextEditor";
import PointsChip from "@/components/courses/PointsChip";
import CourseAssistant from "@/components/courses/CourseAssistant";
import { CourseWalkthrough } from "@/components/courses/CourseWalkthrough.tsx";
import {
  buildWalkthrough,
  flattenWalkthrough,
  rememberWalkthrough,
  walkChapterAt,
  walkOutline,
  walkthroughRecord,
  type WalkEditorPanel,
  type WalkStep,
} from "@/components/courses/builderWalkthrough";
import CourseAnnounceDialog from "@/components/courses/CourseAnnounceDialog";
import QuestionEditor, { type QuestionRecord } from "@/components/courses/QuestionEditor";
import KindBadge from "@/components/courses/KindBadge";
import SlideDeckEditor from "@/components/courses/SlideDeckEditor";
import StudentPreviewDialog from "@/components/courses/StudentPreviewDialog";
import { unitPlacementOf, type UnitPlacement } from "@/lib/unitPlacement";
import CoursePreviewDialog from "@/components/courses/CoursePreviewDialog";
import CourseBreakerDialog from "@/components/courses/CourseBreakerDialog";
import { ProposalSyllabusDiff } from "@/components/courses/ProposalSyllabusPages";
import { refreshProposalNavCounts } from "@/components/courses/ProposalNavCount";
import { courseBreakerQueryOn, treeFromCourseStructure } from "@/lib/courseBreaker";
import StudentActivityPreview from "@/components/student/StudentActivityPreview";
import { promptPreview, scoringBadge, questionTypeCaption, questionIncompleteReasons, questionIsComplete } from "@/lib/questionScoring";
import {
  DISCUSSION_BOARD_KEYS,
  DISCUSSION_BOARD_META,
  HOUR_BUCKETS,
  INCLUDE_SETTING_KEYS,
  INCLUDE_SETTING_META,
  SETTING_META,
  activityHasQuestions,
  activityRequireKnowledgeOn,
  activityIncludes,
  activityStudentMayMessage,
  activitySections,
  activitySpec,
  assignmentPointsOf,
  defaultPointsAward,
  pointsAwardOf,
  completionAwardOf,
  type PointsAward,
  bodyHasRequiredMedia,
  incompleteReasons,
  requiredMediaPart,
  continuePrerequisites,
  normalizeContinueSettings,
  resolveSubmissionSettings,
  normalizeDiscussionBoard,
  mediaBoxStyle,
  mediaSizeFrom,
  partHasContent,
  partsFromBody,
  sanitizeMediaPx,
  videoPlayback,
  visibleSettings,
  type ActivityIncludes,
  type BodyPart,
  type ContinueSettings,
  type SubmissionSettings,
  type IncludeSettingKey,
  type MediaSize,
  type SettingKey,
} from "@/lib/activityAnatomy";
import { type MinuteTypeSplits } from "@/lib/minuteCategoryAwards";
import { embedVideoParts, lockableVideoParts } from "@/lib/mediaWatch";
import { ApiError, api, del, errorMessage, patch, post } from "@/lib/api";
import { listMyMedia, uploadLearnMedia, type LearnMedia } from "@/lib/media";
import {
  deckHasSource,
  inferSlideMode,
  emptySlideDeck,
  normalizeSlideDecks,
  type OutlineMark,
  type OutlineNode,
  type OutlineTarget,
  type SlideDeck,
  type SlideMode,
} from "@/lib/slideDecks";
import { accessDeniedNavigateState } from "@/utils/accessDenied";
import "./CourseBuilder.css";
import "./Courses.css";

/**
 * Free-form course builder (port of the original AcuityLearn builder).
 * Authors add units, lessons, and items as they see fit; "Use standard
 * template" pre-fills an empty course with Dr. Wynder's 9x4x6 anatomy.
 */

// ---------------------------------------------------------------------------
// Types + item vocabulary (mirrors constants/courseAnatomy.js)
// ---------------------------------------------------------------------------

interface ItemTypeSpec {
  type: string;
  label: string;
  description: string;
  icon: ComponentType<{ size?: number | string; className?: string }>;
}

const ITEM_TYPES: ItemTypeSpec[] = [
  { type: "assessment", label: "Assessment", description: "Formative checks with question builder", icon: ClipboardList },
  { type: "audio", label: "Audio", description: "Recordings or linked audio sources", icon: Headphones },
  { type: "discussion", label: "Discussion", description: "An open-ended prompt students respond to and discuss", icon: MessageSquare },
  { type: "exam", label: "Exam", description: "Summative exam with attempts and time limit", icon: GraduationCap },
  { type: "homework", label: "Homework", description: "Outside-class practice with the question builder", icon: BookOpen },
  { type: "key_terms", label: "Key terms", description: "Type terms with definitions", icon: BookMarked },
  { type: "lab", label: "Lab", description: "Hands-on lab with materials", icon: FlaskConical },
  { type: "project", label: "Project", description: "Extended project with submission", icon: FolderKanban },
  { type: "reading", label: "Reading", description: "Articles, PDFs, primary sources, or inline content", icon: FileText },
  { type: "slides", label: "Slides", description: "A Canva or PDF study, on this item or mapped onto lessons", icon: Presentation },
  { type: "video", label: "Video", description: "Tutorial or intro video for a lesson", icon: Video },
];

const iconFor = (type: string) => ITEM_TYPES.find((t) => t.type === type)?.icon || FileText;

/** The picker reads better split by what the item is for than as one alphabetical wall. */
const TYPE_GROUPS: { label: string; types: string[] }[] = [
  { label: "Content", types: ["reading", "video", "audio", "slides", "key_terms"] },
  { label: "Graded", types: ["assessment", "exam", "homework", "project", "lab", "discussion"] },
];

type ContainerFieldSpec = { label: string; hint?: string };

/**
 * Container tiers, mirroring constants/courseAnatomy.js CONTAINER_TYPES.
 * `fields` is which extras this tier shows; title/publish/position are always on.
 */
const CONTAINER_TIERS: {
  type: string;
  label: string;
  rank: number;
  fields: Record<string, ContainerFieldSpec>;
}[] = [
  {
    type: "unit",
    label: "Unit",
    rank: 0,
    fields: {
      objectives: { label: "Unit objectives", hint: "one per line · students see these on Path" },
      coreQuestion: { label: "Core question" },
      standardCodes: { label: "Standards" },
    },
  },
  {
    type: "lesson",
    label: "Lesson",
    rank: 1,
    fields: {
      objectives: { label: "Lesson objectives", hint: "one per line" },
      standardCodes: { label: "Sub-standards" },
    },
  },
];

const tierSpec = (type: string) => CONTAINER_TIERS.find((t) => t.type === type) || null;

/** The tier that goes inside `type`, or null at the deepest one. */
const childTierOf = (type: string) => {
  const parent = CONTAINER_TIERS.find((t) => t.type === type);
  if (!parent) return null;
  return (
    CONTAINER_TIERS.filter((t) => t.rank > parent.rank).sort((a, b) => a.rank - b.rank)[0] || null
  );
};

const GRADE_LEVELS = ["TK", "K", ...Array.from({ length: 12 }, (_, i) => String(i + 1))];

const WEIGHT_FIELDS: { key: string; label: string }[] = [
  { key: "exams", label: "Unit exams" },
  { key: "assignments", label: "Assignments" },
  { key: "quizzes", label: "Quizzes" },
  { key: "research", label: "Research project" },
  { key: "discussion", label: "Discussion" },
];

interface Item {
  id: string;
  type: string;
  label: string;
  title: string | null;
  body: BodyPart[];
  content: Record<string, unknown>;
  metadata: Record<string, unknown>;
  required: boolean;
  requirePrevious: boolean;
  estimatedMinutes: number;
  primaryTaxonomy?: string | null;
  secondaryTaxonomy?: string | null;
  tertiaryTaxonomy?: string | null;
  secondaryMinutes?: number | null;
  tertiaryMinutes?: number | null;
  minuteTypeSplits?: MinuteTypeSplits;
  totalPoints: number;
  timeLimitMinutes: number;
  maxAttempts: number | null;
  continueSettings?: ContinueSettings;
  submissionSettings?: SubmissionSettings;
  position: number;
  holdsQuestions: boolean;
  questionCount: number;
  questionsIncomplete?: number;
  published: boolean;
  status?: string;
  dueAt: string | null;
  complete: boolean;
  targets: { minWords: number | null; maxWords: number | null; minItems: number | null; maxItems: number | null } | null;
}

interface Progress { complete: number; total: number }

/**
 * One node shape at every tier of the outline. A unit and a lesson differ only
 * by `type`, which selects a word — so the outline renders with one recursive
 * component instead of a copy of itself per level.
 */
interface Container {
  id: string;
  type: string;
  label: string;
  typeLabel: string;
  title: string | null;
  coreQuestion: string | null;
  published: boolean;
  status?: string;
  objectives: string[];
  taxonomyIds: string[];
  content: Record<string, unknown>;
  activities: Item[];
  children: Container[];
  progress: Progress;
}

interface Structure {
  course: {
    id: string;
    title: string;
    description: string | null;
    isMaster: boolean;
    editable: boolean;
    writeMode?: "edit" | "propose" | "none";
    canReview?: boolean;
    canPropose?: boolean;
    modLock?: boolean;
    pendingProposals?: number;
    ownerSchoolId?: string | null;
    primaryTaxonomy: string | null;
    gradeLevels: string[];
    gradeWeights: Record<string, number>;
    gradeRubric?: {
      id: string;
      title: string;
      isFoundation: boolean;
      courseCount: number;
      categories: {
        label: string;
        activityTypes: string[];
        measure: "points" | "minutes";
        weight: number;
        cutWeight: number | null;
      }[];
    } | null;
    hasActivityProgress?: boolean;
    objectives: string[];
    syllabus?: { mediaId: string; url: string; name: string; mime?: string } | null;
  };
  empty: boolean;
  activities: Item[];
  containers: Container[];
  progress: Progress;
}

type Question = QuestionRecord;

interface KeyTerm { id: string; term: string; definition: string }

function flattenOutline(structure: Structure): OutlineTarget[] {
  const out: OutlineTarget[] = [
    { id: structure.course.id, kind: "course", label: structure.course.title || "This course" },
  ];
  const walk = (nodes: Container[], trail: string) => {
    for (const node of nodes) {
      const name = node.title?.trim() || `Untitled ${node.typeLabel}`;
      const label = trail ? `${trail} / ${node.label} · ${name}` : `${node.label} · ${name}`;
      out.push({ id: node.id, kind: "container", label });
      for (const item of node.activities) {
        out.push({
          id: item.id,
          kind: "item",
          label: `${label} / ${item.title?.trim() || item.label}`,
        });
      }
      walk(node.children, label);
    }
  };
  walk(structure.containers, "");
  for (const item of structure.activities) {
    out.push({
      id: item.id,
      kind: "item",
      label: `${structure.course.title || "Course"} / ${item.title?.trim() || item.label}`,
    });
  }
  return out;
}

function markForContainer(type: string): OutlineMark {
  return type === "lesson" ? "L" : "U";
}

function itemOutlineNode(item: Item): OutlineNode {
  return {
    id: item.id,
    kind: "item",
    mark: "I",
    label: item.label,
    title: item.title?.trim() || item.label,
    children: [],
  };
}

function containerOutlineNode(node: Container): OutlineNode {
  return {
    id: node.id,
    kind: "container",
    mark: markForContainer(node.type),
    label: node.label,
    title: node.title?.trim() || `Untitled ${node.typeLabel}`,
    children: [...node.children.map(containerOutlineNode), ...node.activities.map(itemOutlineNode)],
  };
}

function outlineTree(structure: Structure): OutlineNode[] {
  return [
    {
      id: structure.course.id,
      kind: "course",
      mark: "C",
      label: "Course",
      title: structure.course.title || "This course",
      children: [
        ...structure.containers.map(containerOutlineNode),
        ...structure.activities.map(itemOutlineNode),
      ],
    },
  ];
}

const newLocalId = () => `t-${Math.random().toString(36).slice(2, 10)}`;
type DraftPart = BodyPart & { key: string };

function emptyHtmlPart(): DraftPart {
  return { key: newLocalId(), type: "html", html: "" };
}

function toDraftPart(part: BodyPart): DraftPart {
  return { ...part, key: newLocalId() };
}

function fromDraftPart(part: DraftPart): BodyPart {
  if (part.type === "html") return { type: "html", html: part.html };
  if (part.type === "image") {
    return {
      type: "image",
      url: part.url,
      ...(part.alt ? { alt: part.alt } : {}),
      ...(part.mediaId ? { mediaId: part.mediaId } : {}),
      ...mediaSizeFrom(part),
    };
  }
  if (part.type === "audio") {
    return {
      type: "audio",
      url: part.url,
      ...(part.mediaId ? { mediaId: part.mediaId } : {}),
    };
  }
  if (part.type === "file") {
    return {
      type: "file",
      url: part.url,
      name: part.name || "File",
      ...(part.mime ? { mime: part.mime } : {}),
      ...(part.mediaId ? { mediaId: part.mediaId } : {}),
    };
  }
  return {
    type: "video",
    url: part.url,
    ...(part.mediaId ? { mediaId: part.mediaId } : {}),
    ...mediaSizeFrom(part),
  };
}

function defaultDraftParts(type: string, body: unknown): DraftPart[] {
  const stored = partsFromBody(body);
  if (stored.length) return stored.map(toDraftPart);
  const parts: DraftPart[] = [emptyHtmlPart()];
  const need = requiredMediaPart(type);
  if (need) parts.push({ key: newLocalId(), type: need, url: "" });
  return parts;
}

const countWords = (text: string) => text.trim().split(/\s+/).filter(Boolean).length;

function htmlToPlain(html: string) {
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

/** ISO instant -> the local "YYYY-MM-DDTHH:mm" a datetime-local input expects. */
function toLocalInput(iso: string | null) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** Short, readable due date for an outline row. */
function formatDue(iso: string | null) {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

/** Existing plain-text bodies predate rich text — show them as paragraphs, escaped. */
function escapeToHtml(text: string) {
  if (!text) return "";
  const esc = text
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
  return esc
    .split(/\n{2,}/)
    .map((para) => `<p>${para.replace(/\n/g, "<br>")}</p>`)
    .join("");
}

// ---------------------------------------------------------------------------
// Small shared pieces
// ---------------------------------------------------------------------------

function ProgressPill({ progress }: { progress: Progress }) {
  if (progress.total === 0) return null;
  const done = progress.complete === progress.total;
  return (
    <span className={`al-cb-pill${done ? " al-cb-pill--done" : ""}`}>
      {progress.complete}/{progress.total}
    </span>
  );
}


/**
 * Row actions behind a single kebab, the way Canvas does it. A lesson with six
 * items used to render ~30 always-visible controls; now it renders six.
 */
/**
 * Canvas-style publish control: an icon, not another text badge. Rows are already
 * busy, and one click to toggle beats opening a menu for the commonest action.
 */
function outlineItemGaps(item: Item): string[] {
  const reasons = incompleteReasons({
    type: item.type,
    body: item.body,
    content: item.content,
    metadata: item.metadata,
    questionCount: item.questionCount,
  });
  const n = Number(item.questionsIncomplete) || 0;
  if (n > 0 && !reasons.some((row) => /questions? are still incomplete|question is still incomplete/.test(row))) {
    reasons.push(n === 1 ? "1 question is still incomplete." : `${n} questions are still incomplete.`);
  }
  return reasons;
}

function PublishToggle({
  published,
  disabled,
  label,
  title,
  onToggle,
}: {
  published: boolean;
  disabled: boolean;
  label: string;
  title?: string;
  onToggle: () => void;
}) {
  return (
    <button
      type="button"
      className={`al-cb-publish${published ? " al-cb-publish--on" : ""}`}
      disabled={disabled}
      aria-pressed={published}
      title={
        title ||
        (published
          ? `${label} is published — click to unpublish`
          : `${label} is a draft — click to publish`)
      }
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
    >
      {published ? <CheckCircle2 size={15} /> : <Circle size={15} />}
    </button>
  );
}

function RowMenu({
  onMoveUp,
  onMoveDown,
  onDelete,
  disabled,
  label,
  hideReorder,
  placement,
  onPlace,
}: {
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDelete: () => void;
  disabled: boolean;
  label: string;
  hideReorder?: boolean;
  /** Set for an item on a unit that has lessons: where it sits now. */
  placement?: UnitPlacement | null;
  onPlace?: (next: UnitPlacement) => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement | null>(null);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  const run = (fn: () => void) => () => {
    setOpen(false);
    fn();
  };

  return (
    <span className="al-cb-menu" ref={ref}>
      <button
        type="button"
        className="al-cb-icon-btn"
        disabled={disabled}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${label} actions`}
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
      >
        <MoreVertical size={14} />
      </button>
      {open ? (
        <div className="al-cb-menu-pop" role="menu">
          {hideReorder ? null : (
            <>
              <button type="button" role="menuitem" onClick={run(onMoveUp)}>
                <ArrowUp size={13} aria-hidden="true" />
                Move up
              </button>
              <button type="button" role="menuitem" onClick={run(onMoveDown)}>
                <ArrowDown size={13} aria-hidden="true" />
                Move down
              </button>
              {placement && onPlace ? (
                <button
                  type="button"
                  role="menuitem"
                  onClick={run(() => onPlace(placement === "before" ? "after" : "before"))}
                >
                  {placement === "before" ? (
                    <ArrowDownToLine size={13} aria-hidden="true" />
                  ) : (
                    <ArrowUpToLine size={13} aria-hidden="true" />
                  )}
                  {placement === "before" ? "Move below the lessons" : "Move above the lessons"}
                </button>
              ) : null}
            </>
          )}
          <button
            type="button"
            role="menuitem"
            className="al-cb-menu-pop-danger"
            onClick={run(onDelete)}
          >
            <Trash2 size={13} aria-hidden="true" />
            Delete
          </button>
        </div>
      ) : null}
    </span>
  );
}

const LAYER_ICONS: Record<string, ComponentType<{ size?: number | string }>> = {
  unit: LayoutTemplate,
  lesson: BookOpen,
  item: FileText,
};

/** Same dashed ghost as the add-plus, with a spinner while a row is created. */
function CreatingGhost() {
  return (
    <div className="al-cb-add-ghost al-cb-add-ghost--busy" role="status" aria-label="Adding">
      <Loader2 size={18} className="al-cb-add-spin" aria-hidden="true" />
    </div>
  );
}

function addLayerDesc(tierType: string, parentWord: string) {
  if (tierType === "unit") return "A block that can hold lessons and items";
  if (tierType === "lesson") {
    return parentWord === "course" ? "Sits on the course; holds items" : "A block that holds items";
  }
  return parentWord === "course"
    ? "Hangs off the course — a project, reading, exam…"
    : "A project, reading, exam…";
}

/**
 * Pick what to add under a parent. Course offers Unit / Lesson / Item; a unit
 * filters Unit out so the dialog is Lesson / Item.
 */
function AddToParentButton({
  disabled,
  parentWord,
  containerTypes,
  onPickContainer,
  onPickItem,
  trigger = "ghost",
  tourButton,
  forceOpen = false,
}: {
  disabled: boolean;
  parentWord: string;
  containerTypes: string[];
  onPickContainer: (type: string) => void;
  onPickItem: () => void;
  trigger?: "ghost" | "link";
  tourButton?: string;
  forceOpen?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const title = `Add to ${parentWord}`;
  const tiers = CONTAINER_TIERS.filter((tier) => containerTypes.includes(tier.type));

  useEffect(() => {
    setOpen(forceOpen);
  }, [forceOpen]);

  const pick = (fn: () => void) => {
    setOpen(false);
    fn();
  };

  return (
    <span className={trigger === "ghost" ? "al-cb-add-ghost-wrap" : undefined}>
      <button
        type="button"
        className={trigger === "ghost" ? "al-cb-add-ghost" : "al-cb-add-link"}
        disabled={disabled}
        data-tour={tourButton}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={title}
        onClick={() => setOpen(true)}
      >
        <Plus size={trigger === "ghost" ? 18 : 13} aria-hidden="true" />
        {trigger === "link" ? title : null}
      </button>
      <NotificationModal
        isOpen={open}
        type="neutral"
        icon={<Plus />}
        title={title}
        message={
          parentWord === "course"
            ? "A unit, a lesson, or an item can sit on the course itself."
            : `A lesson or an item can sit in this ${parentWord}.`
        }
        overlay
        overlayClose
        showOkButton={false}
        align="center"
        iconSize="2.25rem"
        titleSize="1.05rem"
        style={COURSE_DIALOG_STYLE}
        onClose={() => setOpen(false)}
      >
        <div className="al-cb-choice-list">
          {tiers.map((tier) => {
            const Icon = LAYER_ICONS[tier.type] || LayoutTemplate;
            return (
              <button
                key={tier.type}
                type="button"
                className="al-cb-choice"
                data-tour={`cb-pick-${tier.type}`}
                onClick={() => pick(() => onPickContainer(tier.type))}
              >
                <span className="al-cb-choice-icon"><Icon size={17} /></span>
                <span>
                  <span className="al-cb-choice-title">{tier.label}</span>
                  <span className="al-cb-choice-desc">{addLayerDesc(tier.type, parentWord)}</span>
                </span>
              </button>
            );
          })}
          <button type="button" className="al-cb-choice" data-tour="cb-pick-item" onClick={() => pick(onPickItem)}>
            <span className="al-cb-choice-icon"><FileText size={17} /></span>
            <span>
              <span className="al-cb-choice-title">Item</span>
              <span className="al-cb-choice-desc">{addLayerDesc("item", parentWord)}</span>
            </span>
          </button>
        </div>
      </NotificationModal>
    </span>
  );
}

/** Inline title input — uncontrolled, saved on blur, remounted per value via key. */
function InlineTitle({
  value,
  placeholder,
  disabled,
  onSave,
  className,
  saved,
  editOnDoubleClick,
  onIdleClick,
  tour,
}: {
  value: string | null;
  placeholder: string;
  disabled: boolean;
  onSave: (next: string) => void;
  className?: string;
  saved?: boolean;
  /** Outline headers: single click expands; double-click opens the field. */
  editOnDoubleClick?: boolean;
  /** Fired on a single click when editOnDoubleClick is on (after a short wait). */
  onIdleClick?: () => void;
  tour?: string;
}) {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const idleClickTimer = useRef<number | null>(null);
  const fieldClass = className || "al-cb-inline-input";
  const empty = !(value ?? "").trim();

  useEffect(() => {
    return () => {
      if (idleClickTimer.current) window.clearTimeout(idleClickTimer.current);
    };
  }, []);

  useEffect(() => {
    if (!editing) return;
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    el.select();
  }, [editing]);

  const finish = (next: string) => {
    if (next !== (value ?? "")) onSave(next);
    setEditing(false);
  };

  const cancelIdleClick = () => {
    if (idleClickTimer.current) {
      window.clearTimeout(idleClickTimer.current);
      idleClickTimer.current = null;
    }
  };

  if (editOnDoubleClick && !editing) {
    return (
      <span className="al-cb-inline-wrap" data-tour={tour}>
        <span
          className={`${fieldClass} al-cb-inline-display${empty ? " al-cb-inline-display--ph" : ""}`}
          title={disabled ? undefined : "Double-click to rename"}
          onClick={(e) => {
            e.stopPropagation();
            cancelIdleClick();
            idleClickTimer.current = window.setTimeout(() => {
              idleClickTimer.current = null;
              onIdleClick?.();
            }, 280);
          }}
          onDoubleClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            cancelIdleClick();
            if (!disabled) setEditing(true);
          }}
        >
          {empty ? placeholder : value}
        </span>
        {saved ? (
          <span className="al-cb-saved" title="Saved">
            <Check size={13} />
          </span>
        ) : null}
      </span>
    );
  }

  return (
    <span className="al-cb-inline-wrap" data-tour={tour}>
      <input
        ref={inputRef}
        key={editOnDoubleClick ? undefined : (value ?? "")}
        className={fieldClass}
        defaultValue={value ?? ""}
        placeholder={placeholder}
        disabled={disabled}
        onClick={editOnDoubleClick ? (e) => e.stopPropagation() : undefined}
        onBlur={(e) => finish(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") (e.target as HTMLInputElement).blur();
          if (e.key === "Escape" && editOnDoubleClick) {
            e.preventDefault();
            setEditing(false);
          }
        }}
      />
      {saved ? (
        <span className="al-cb-saved" title="Saved">
          <Check size={13} />
        </span>
      ) : null}
    </span>
  );
}

/** Objectives / notes textarea — uncontrolled, saved on blur. */
function InlineTextarea({
  value,
  placeholder,
  disabled,
  rows = 2,
  onSave,
}: {
  value: string;
  placeholder: string;
  disabled: boolean;
  rows?: number;
  onSave: (next: string) => void;
}) {
  return (
    <textarea
      key={value}
      className="al-cb-textarea"
      rows={rows}
      defaultValue={value}
      placeholder={placeholder}
      disabled={disabled}
      onBlur={(e) => {
        if (e.target.value !== value) onSave(e.target.value);
      }}
    />
  );
}

function isProposed(result: unknown): boolean {
  if (!result || typeof result !== "object") return false;
  const r = result as { proposed?: boolean; data?: { proposed?: boolean } };
  return Boolean(r.proposed || r.data?.proposed);
}

const AUTO_SAVED_LABELS: Record<string, string> = {
  title: "Title saved",
  objectives: "Objectives saved",
  coreQuestion: "Question saved",
  taxonomyIds: "Standards saved",
};

function autoSavedLabel(body: Record<string, unknown>, proposed: boolean) {
  if (proposed) return "Proposed";
  const keys = Object.keys(body);
  if (keys.length === 1 && AUTO_SAVED_LABELS[keys[0]]) return AUTO_SAVED_LABELS[keys[0]];
  return "Saved";
}

function isProposedNode(node: { status?: string }) {
  return node.status === "proposed";
}

function writeActionLabel(proposeOnly: boolean, saving: boolean, saveIdle: string) {
  if (saving) return proposeOnly ? "Proposing…" : "Saving…";
  return proposeOnly ? saveIdle.replace(/^Save/, "Propose") : saveIdle;
}

type CourseProposal = {
  id: string;
  courseId?: string;
  kind: string;
  targetId?: string | null;
  action: string;
  payload?: Record<string, unknown>;
  status?: string;
  createdAt?: string;
};

const PROPOSAL_SKIP_KEYS = new Set(["courseId", "parentId", "containerId", "actor", "template", "gradeRubricId"]);

const PROPOSAL_FIELD_LABELS: Record<string, string> = {
  title: "Title",
  type: "Type",
  objectives: "Objectives",
  taxonomyIds: "Standards",
  coreQuestion: "Core question",
  body: "Body",
  prompt: "Prompt",
  published: "Published",
  required: "Required",
  requirePrevious: "Require previous",
  estimatedMinutes: "Estimated minutes",
  hourBucket: "Hour bucket",
  hourSplits: "Hour types",
  minuteTypeSplits: "Minute types",
  secondaryTaxonomy: "Secondary category",
  tertiaryTaxonomy: "Tertiary category",
  dueAt: "Due",
  timeLimitMinutes: "Time limit (minutes)",
  description: "Description",
  gradeLevels: "Grade levels",
  gradeWeights: "Grade weights",
  gradeRubricTitle: "Grade rubric",
  syllabusMediaId: "Syllabus",
  maxAttempts: "Max attempts",
  content: "Content",
  metadata: "Metadata",
  direction: "Direction",
  toIndex: "Move to",
};

function findContainer(nodes: Container[], id: string): Container | null {
  for (const node of nodes) {
    if (node.id === id) return node;
    const nested = findContainer(node.children, id);
    if (nested) return nested;
  }
  return null;
}

function findActivity(structure: Structure, id: string): Item | null {
  const top = structure.activities.find((row) => row.id === id);
  if (top) return top;
  const walk = (nodes: Container[]): Item | null => {
    for (const node of nodes) {
      const hit = node.activities.find((row) => row.id === id);
      if (hit) return hit;
      const nested = walk(node.children);
      if (nested) return nested;
    }
    return null;
  };
  return walk(structure.containers);
}

function nodeLabel(structure: Structure, id: string | null | undefined, kind: string) {
  if (!id) return kind;
  if (kind === "course" || id === structure.course.id) return structure.course.title;
  const container = findContainer(structure.containers, id);
  if (container) return container.title?.trim() || container.label;
  const item = findActivity(structure, id);
  if (item) return item.title?.trim() || item.label;
  return kind;
}

function kindWord(kind: string, payload: Record<string, unknown>) {
  const type = typeof payload.type === "string" ? payload.type : "";
  if (kind === "container") return (CONTAINER_TIERS.find((row) => row.type === type)?.label || type || "container").toLowerCase();
  if (kind === "activity") return (ITEM_TYPES.find((row) => row.type === type)?.label || type || "item").toLowerCase();
  if (kind === "question") return "question";
  if (kind === "course") return "course";
  if (kind === "template") return "standard structure";
  return kind;
}

function proposalHeadline(row: CourseProposal, structure: Structure) {
  const payload = row.payload && typeof row.payload === "object" ? row.payload : {};
  const title = typeof payload.title === "string" ? payload.title.trim() : "";
  const quoted = title ? ` “${title}”` : "";
  const word = kindWord(row.kind, payload);
  const parentName = nodeLabel(structure, row.targetId, row.kind);

  if (row.action === "create") {
    if (row.kind === "template") return "Add the standard unit / lesson structure";
    return parentName && parentName !== row.kind ? `Add ${word}${quoted} in ${parentName}` : `Add ${word}${quoted}`;
  }
  if (row.action === "delete") return `Delete ${parentName}`;
  if (row.action === "publish") {
    const on = payload.published === false ? "Unpublish" : "Publish";
    return `${on} ${parentName}`;
  }
  if (row.action === "move") return `Move ${parentName}`;
  if (row.action === "update") {
    const keys = Object.keys(payload).filter((key) => !PROPOSAL_SKIP_KEYS.has(key));
    const field = keys.length === 1 ? PROPOSAL_FIELD_LABELS[keys[0]] || keys[0] : "";
    return field ? `Update ${field.toLowerCase()} on ${parentName}` : `Update ${parentName}`;
  }
  return `${row.action} ${word}${quoted}`;
}

function formatWeights(value: unknown): string {
  const raw = value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : {};
  const any = WEIGHT_FIELDS.some((field) => raw[field.key] != null && raw[field.key] !== "");
  if (!any) return "Not set";
  return WEIGHT_FIELDS.map((field) => `${field.label} ${Number(raw[field.key]) || 0}`).join(", ");
}

function formatProposalValue(key: string, value: unknown): string {
  if (key === "gradeWeights") return formatWeights(value);
  if (key === "gradeRubricTitle") return value == null || value === "" ? "None" : String(value);
  if (key === "gradeLevels" || key === "objectives") {
    const rows = Array.isArray(value) ? value.map((item) => String(item).trim()).filter(Boolean) : [];
    return rows.length ? rows.join("\n") : "—";
  }
  if (value == null || value === "") return "—";
  if (typeof value === "boolean") return value ? "Yes" : "No";
  if (Array.isArray(value)) {
    if (key === "body" || (value.length > 0 && value[0] && typeof value[0] === "object" && "type" in (value[0] as object))) {
      return value
        .map((part) => {
          if (!part || typeof part !== "object") return "";
          const p = part as { type?: string; html?: string; url?: string };
          if (p.type === "html") return htmlToPlain(p.html || "");
          return p.url || p.type || "";
        })
        .filter(Boolean)
        .join("\n");
    }
    return value.map((item) => String(item)).filter(Boolean).join("\n") || "—";
  }
  if (typeof value === "object") {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }
  if (key === "type") {
    const type = String(value);
    return ITEM_TYPES.find((row) => row.type === type)?.label
      || CONTAINER_TIERS.find((row) => row.type === type)?.label
      || type;
  }
  const text = String(value);
  if (key === "body" || /html/i.test(key) || text.includes("<")) return htmlToPlain(text) || text;
  return text;
}

function liveFieldValue(structure: Structure, row: CourseProposal, key: string): string | null {
  if (row.action !== "update" && row.action !== "delete" && row.action !== "publish") return null;
  const id = row.targetId;
  if (!id) return null;
  if (row.kind === "course") {
    if (key === "title") return structure.course.title;
    if (key === "description") return structure.course.description || "—";
    if (key === "objectives") return structure.course.objectives.join("\n") || "—";
    if (key === "gradeLevels") return structure.course.gradeLevels.join("\n") || "—";
    if (key === "gradeWeights") return formatWeights(structure.course.gradeWeights);
    if (key === "syllabusMediaId") return structure.course.syllabus?.mediaId || "";
  }
  const container = findContainer(structure.containers, id);
  if (container) {
    if (key === "title") return container.title || "—";
    if (key === "objectives") return container.objectives.join("\n") || "—";
    if (key === "taxonomyIds" || key === "standardCodes") {
      const ids = container.taxonomyIds || [];
      return ids.length ? ids.join("\n") : "—";
    }
    if (key === "coreQuestion") return container.coreQuestion || "—";
    if (key === "published") return container.published ? "Yes" : "No";
  }
  const item = findActivity(structure, id);
  if (item) {
    if (key === "title") return item.title || "—";
    if (key === "body") return formatProposalValue("body", item.body);
    if (key === "published") return item.published ? "Yes" : "No";
    if (key === "required") return item.required ? "Yes" : "No";
    if (key === "requirePrevious") return item.requirePrevious ? "Yes" : "No";
    if (key === "estimatedMinutes") return String(item.estimatedMinutes);
    if (key === "dueAt") return item.dueAt || "—";
  }
  return null;
}

type ProposalChangeRow = {
  key: string;
  label: string;
  proposed: string;
  current: string | null;
  syllabus?: {
    courseId: string;
    currentId: string | null;
    proposedId: string | null;
  };
};

function proposalChangeRows(row: CourseProposal, structure: Structure): ProposalChangeRow[] {
  const payload = row.payload && typeof row.payload === "object" ? row.payload : {};
  const keys = Object.keys(payload).filter((key) => !PROPOSAL_SKIP_KEYS.has(key));
  if (row.action === "delete") {
    return [
      {
        key: "remove",
        label: "Remove",
        proposed: nodeLabel(structure, row.targetId, row.kind),
        current: null as string | null,
      },
    ];
  }
  if (keys.length === 0) {
    return [
      {
        key: "change",
        label: row.action === "create" ? "New" : "Change",
        proposed: proposalHeadline(row, structure),
        current: null as string | null,
      },
    ];
  }
  const changes = keys.flatMap((key) => {
    if (key === "syllabusMediaId") {
      const currentId = structure.course.syllabus?.mediaId || "";
      const proposedId = payload.syllabusMediaId ? String(payload.syllabusMediaId) : "";
      if (currentId === proposedId) return [];
      return [{
        key,
        label: "Syllabus",
        proposed: proposedId ? "Proposed syllabus" : "Remove the syllabus",
        current: currentId ? "Current syllabus" : "No syllabus yet",
        syllabus: {
          courseId: structure.course.id,
          currentId: currentId || null,
          proposedId: proposedId || null,
        },
      }];
    }
    const proposed = formatProposalValue(key, payload[key]);
    const current = liveFieldValue(structure, row, key);
    if (current != null && current === proposed) return [];
    return [{
      key,
      label: PROPOSAL_FIELD_LABELS[key] || key,
      proposed,
      current,
    }];
  });
  if (!changes.length) {
    return [{
      key: "change",
      label: "Change",
      proposed: proposalHeadline(row, structure),
      current: null as string | null,
    }];
  }
  return changes;
}

function ProposalsPanel({
  courseId,
  structure,
  onChanged,
}: {
  courseId: string;
  structure: Structure;
  onChanged: () => Promise<void>;
}) {
  const { toast, notice } = useNotification();
  const [rows, setRows] = useState<CourseProposal[]>([]);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const loadRows = useCallback(async () => {
    try {
      const envelope = await api<{ success: boolean; data?: CourseProposal[] }>(
        `/api/courses/${encodeURIComponent(courseId)}/proposals`,
      );
      setRows(Array.isArray(envelope.data) ? envelope.data : []);
    } catch (err) {
      notice({
        type: "error",
        title: "Could not load proposals",
        subtext: errorMessage(err),
        duration: 7000,
      });
    }
  }, [courseId, notice]);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  const review = async (id: string, action: "apply" | "reject") => {
    if (action === "reject") {
      const ok = window.confirm(
        "Discard this proposed change?\n\nThe live course is not changed. Proposed new items are only added when you Apply.",
      );
      if (!ok) return;
    }
    setBusyId(id);
    try {
      await post(`/api/courses/${encodeURIComponent(courseId)}/proposals/${encodeURIComponent(id)}/${action}`, {});
      toast.success(action === "apply" ? "Proposal applied" : "Proposal discarded");
      setOpenId(null);
      void refreshProposalNavCounts();
      await loadRows();
      await onChanged();
    } catch (err) {
      notice({
        type: "error",
        title: action === "apply" ? "Could not apply proposal" : "Could not reject proposal",
        subtext: errorMessage(err),
        duration: 7000,
      });
    } finally {
      setBusyId(null);
    }
  };

  if (!rows.length) return null;

  const openRow = rows.find((row) => row.id === openId) ?? null;

  return (
    <section className="al-cb-proposals">
      <h2>Pending proposals ({rows.length})</h2>
      <ul>
        {rows.map((row) => (
          <li key={row.id}>
            <button
              type="button"
              className="al-cb-proposal-summary"
              onClick={() => setOpenId(row.id)}
            >
              <span className="al-cb-proposal-title">{proposalHeadline(row, structure)}</span>
              {row.createdAt ? (
                <span className="al-cb-proposal-when">{new Date(row.createdAt).toLocaleString()}</span>
              ) : null}
            </button>
            <span className="al-cb-proposals-actions">
              <button type="button" className="al-cb-btn al-cb-btn--ghost" disabled={Boolean(busyId)} onClick={() => setOpenId(row.id)}>
                View
              </button>
              <button type="button" className="al-cb-btn al-cb-btn--ghost" disabled={Boolean(busyId)} onClick={() => void review(row.id, "reject")}>
                {busyId === row.id ? "…" : "Reject"}
              </button>
              <button type="button" className="al-cb-btn" disabled={Boolean(busyId)} onClick={() => void review(row.id, "apply")}>
                {busyId === row.id ? "…" : "Apply"}
              </button>
            </span>
          </li>
        ))}
      </ul>

      <Dialog
        open={Boolean(openRow)}
        title={openRow ? proposalHeadline(openRow, structure) : "Proposal"}
        subtitle={
          openRow?.createdAt
            ? `${openRow.action} ${openRow.kind} · ${new Date(openRow.createdAt).toLocaleString()}`
            : openRow
              ? `${openRow.action} ${openRow.kind}`
              : undefined
        }
        width="34rem"
        maxHeight="80vh"
        onClose={() => setOpenId(null)}
        footer={
          openRow ? (
            <>
              <button
                type="button"
                className="al-cb-btn al-cb-btn--ghost"
                disabled={Boolean(busyId)}
                onClick={() => void review(openRow.id, "reject")}
              >
                {busyId === openRow.id ? "…" : "Reject"}
              </button>
              <button
                type="button"
                className="al-cb-btn"
                disabled={Boolean(busyId)}
                onClick={() => void review(openRow.id, "apply")}
              >
                {busyId === openRow.id ? "…" : "Apply"}
              </button>
            </>
          ) : null
        }
      >
        {openRow ? (
          <>
            <dl className="al-cb-proposal-dl">
              {proposalChangeRows(openRow, structure).map((change) => (
                <div key={change.key} className="al-cb-proposal-row">
                  <dt>{change.label}</dt>
                  <dd>
                    {"syllabus" in change && change.syllabus ? (
                      <ProposalSyllabusDiff
                        courseId={change.syllabus.courseId}
                        currentId={change.syllabus.currentId}
                        proposedId={change.syllabus.proposedId}
                      />
                    ) : change.current != null && change.current !== change.proposed ? (
                      <div className="al-cb-proposal-diff">
                        <p>
                          <span className="al-cb-proposal-diff-label">Now</span>
                          {change.current}
                        </p>
                        <p>
                          <span className="al-cb-proposal-diff-label">Proposed</span>
                          {change.proposed}
                        </p>
                      </div>
                    ) : (
                      <p className="al-cb-proposal-value">{change.proposed}</p>
                    )}
                  </dd>
                </div>
              ))}
            </dl>
            {openRow.action === "create" ? (
              <p className="al-cb-proposal-note">
                This is not on the live course yet. Apply inserts it. Reject discards it.
              </p>
            ) : openRow.action === "delete" ? (
              <p className="al-cb-proposal-note">
                Apply removes this from the live course. Reject leaves it as it is.
              </p>
            ) : (
              <p className="al-cb-proposal-note">
                Apply writes this onto the live course. Reject leaves the live course as it is.
              </p>
            )}
          </>
        ) : null}
      </Dialog>
    </section>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function itemForWalk(structure: Structure, types: string[]): Item | null {
  const items: Item[] = [];
  const walk = (nodes: Container[]) => {
    for (const node of nodes) {
      items.push(...node.activities);
      walk(node.children);
    }
  };
  walk(structure.containers);
  items.push(...structure.activities);
  for (const type of types) {
    if (type === "*") return items[0] ?? null;
    const hit = items.find((item) => item.type === type);
    if (hit) return hit;
  }
  return null;
}

function firstUnit(structure: Structure) {
  return structure.containers.find((node) => node.type === "unit") || structure.containers[0] || null;
}

function firstLesson(structure: Structure) {
  const unit = firstUnit(structure);
  if (!unit) return null;
  return unit.children.find((node) => node.type === "lesson") || unit.children[0] || null;
}

export default function CourseBuilder() {
  const { courseId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { pageAccess, capabilities, loading: authLoading, user } = useAuth();
  const { toast, notice } = useNotification();

  const canOpen = Boolean(pageAccess?.['course-builder']);
  const showBreaker = courseBreakerQueryOn(searchParams.toString()) && Boolean(capabilities?.courseBreaker);

  const [structure, setStructure] = useState<Structure | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // Parent id (`"course"` at the root) while a unit, lesson, or item is posting.
  const [creating, setCreating] = useState<string | null>(null);
  const [announceOpen, setAnnounceOpen] = useState(false);
  // Rows that just arrived — drives the enter animation, then clears.
  const [justCreated, setJustCreated] = useState<Record<string, boolean>>({});
  // Units and lessons start collapsed so a course loads as a short list of rows.
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  // Ids that just saved — drives the brief check mark on inline fields.
  const [savedFlash, setSavedFlash] = useState<Record<string, boolean>>({});
  const [taxonomyOptions, setTaxonomyOptions] = useState<TaxonomyOption[]>([]);
  const [standardOptions, setStandardOptions] = useState<TaxonomyOption[]>([]);
  const [taxonomyLoading, setTaxonomyLoading] = useState(true);

  // Dialog state
  // Drag state: rows only reorder among siblings of the same kind and parent.
  const [drag, setDrag] = useState<{ kind: string; parent: string; id: string } | null>(null);
  const [dropTarget, setDropTarget] = useState<string | null>(null);
  // `containerId` absent means the item hangs directly off the course.
  const [typePickerFor, setTypePickerFor] = useState<{ containerId?: string } | null>(null);
  // Canvas-style: name a unit or lesson at creation instead of leaving a wall of "Untitled".
  const [namePrompt, setNamePrompt] = useState<{
    title: string;
    label: string;
    placeholder: string;
    onSubmit: (name: string) => void;
  } | null>(null);
  const [pendingDelete, setPendingDelete] = useState<{
    kind: "item" | "container";
    id: string;
    title: string;
    message: string;
    errorLabel: string;
    successLabel: string;
  } | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const [assistantOpen, setAssistantOpen] = useState(false);
  const [breakerOpen, setBreakerOpen] = useState(false);
  const [coursePreviewOpen, setCoursePreviewOpen] = useState(false);
  const [walkPhase, setWalkPhase] = useState<"idle" | "offer" | "tour">("idle");
  const [walkStep, setWalkStep] = useState(0);
  const walkOfferChecked = useRef<string | null>(null);

  const showError = useCallback(
    (title: string, err: unknown) =>
      notice({ type: "error", title, subtext: errorMessage(err), duration: 7000 }),
    [notice],
  );

  const markCreated = useCallback((id: string | undefined) => {
    if (!id) return;
    setJustCreated((prev) => ({ ...prev, [id]: true }));
    window.setTimeout(() => {
      setJustCreated((prev) => {
        if (!prev[id]) return prev;
        const next = { ...prev };
        delete next[id];
        return next;
      });
    }, 700);
  }, []);

  const load = useCallback(async () => {
    if (!courseId) return;
    try {
      const envelope = await api<{ success: boolean; data: Structure }>(
        `/api/courses/${encodeURIComponent(courseId)}/structure`,
      );
      setStructure(envelope.data);
    } catch (err) {
      showError("Could not load the course", err);
    } finally {
      setLoading(false);
    }
  }, [courseId, showError]);

  useEffect(() => {
    if (!authLoading && canOpen) void load();
  }, [authLoading, canOpen, load]);

  useEffect(() => {
    if (authLoading || !canOpen) return;
    let cancelled = false;
    (async () => {
      try {
        const [primary, standards] = await Promise.all([
          api<{ success: boolean; data: TaxonomyOption[] }>("/api/courses/taxonomy"),
          api<{ success: boolean; data: TaxonomyOption[] }>("/api/courses/taxonomy?type=standard"),
        ]);
        if (cancelled) return;
        setTaxonomyOptions(Array.isArray(primary.data) ? primary.data : []);
        setStandardOptions(Array.isArray(standards.data) ? standards.data : []);
      } catch {
        /* picker still works empty */
      } finally {
        if (!cancelled) setTaxonomyLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [authLoading, canOpen]);

  useEffect(() => {
    setWalkPhase("idle");
    setWalkStep(0);
  }, [courseId]);

  // First authored course only: ask before guiding. Later courses stay quiet
  // unless they open Walkthrough themselves.
  useEffect(() => {
    const course = structure?.course;
    if (!course || !courseId || user?.id == null) return undefined;
    if (course.id !== courseId) return undefined;
    if (walkOfferChecked.current === courseId) return undefined;
    const mode = course.writeMode ?? (course.editable === false ? "none" : "edit");
    if (mode === "none" || walkthroughRecord(user.id)) {
      walkOfferChecked.current = courseId;
      return undefined;
    }
    let cancelled = false;
    const id = courseId;
    (async () => {
      try {
        const envelope = await api<{
          success: boolean;
          selectedSchoolId?: string | null;
          data?: { id?: string }[];
        }>("/api/courses?schoolId=mine");
        if (cancelled) return;
        walkOfferChecked.current = id;
        if (walkthroughRecord(user.id)) return;
        if (envelope.selectedSchoolId !== "mine") return;
        const rows = Array.isArray(envelope.data) ? envelope.data : [];
        const onlyThis = rows.length === 1 && String(rows[0]?.id) === String(id);
        if (onlyThis) setWalkPhase((phase) => (phase === "idle" ? "offer" : phase));
      } catch {
        if (!cancelled) walkOfferChecked.current = id;
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [structure, courseId, user?.id]);

  const canOverrideDefaults = Boolean(capabilities?.overrideActivityDefaults);
  const walkChapters = useMemo(
    () => buildWalkthrough({ canOverrideDefaults }),
    [canOverrideDefaults],
  );
  const walkSteps = useMemo(() => flattenWalkthrough(walkChapters), [walkChapters]);
  const walkCurrent: WalkStep | undefined = walkPhase === "tour" ? walkSteps[walkStep] : undefined;
  const lessonId = structure ? firstLesson(structure)?.id : undefined;
  const walkItemId =
    structure && walkCurrent?.editorTypes?.length
      ? itemForWalk(structure, walkCurrent.editorTypes)?.id ?? ""
      : "";
  const walkWasTour = useRef(false);

  useEffect(() => {
    if (walkStep >= walkSteps.length) setWalkStep(Math.max(0, walkSteps.length - 1));
  }, [walkStep, walkSteps.length]);

  useEffect(() => {
    if (walkPhase !== "tour" || !structure || !walkCurrent?.expand) return;
    const unit = firstUnit(structure);
    if (!unit) return;
    const lesson = walkCurrent.expand === "lesson" ? firstLesson(structure) : null;
    setExpanded((prev) => ({
      ...prev,
      [unit.id]: true,
      ...(lesson ? { [lesson.id]: true } : {}),
    }));
  }, [walkPhase, walkCurrent, structure]);

  useEffect(() => {
    if (walkPhase !== "tour") {
      if (walkWasTour.current) setTypePickerFor(null);
      walkWasTour.current = false;
      return;
    }
    walkWasTour.current = true;
    if (!walkCurrent?.openPicker || !lessonId) {
      setTypePickerFor(null);
      return;
    }
    setTypePickerFor({ containerId: lessonId });
  }, [walkPhase, walkCurrent, lessonId]);

  useEffect(() => {
    if (walkPhase !== "tour") return;
    if (!walkCurrent?.editorTypes?.length) {
      setEditingItemId(null);
      return;
    }
    setEditingItemId(walkItemId || null);
  }, [walkPhase, walkCurrent, walkItemId]);

  const itemById = useMemo(() => {
    const map = new Map<string, Item>();
    const walk = (nodes: Container[]) => {
      for (const node of nodes) {
        for (const item of node.activities) map.set(item.id, item);
        walk(node.children);
      }
    };
    for (const item of structure?.activities || []) map.set(item.id, item);
    walk(structure?.containers || []);
    return map;
  }, [structure]);

  const breakerTree = useMemo(
    () => (structure ? treeFromCourseStructure(structure) : null),
    [structure],
  );

  useEffect(() => {
    const ids = Object.keys(justCreated);
    if (!ids.length) return;
    const id = ids[ids.length - 1];
    const el = document.querySelector(`[data-outline-id="${CSS.escape(id)}"]`);
    el?.scrollIntoView({ block: "nearest", behavior: "smooth" });
  }, [justCreated, structure]);

  const editingItem = editingItemId ? itemById.get(editingItemId) || null : null;

  if (authLoading) return <LoadingOverlay isVisible message="Loading…" />;
  if (!canOpen) {
    return <Navigate to="/" replace state={accessDeniedNavigateState("course-builder")} />;
  }

  const writeMode = structure?.course.writeMode ?? (structure?.course.editable === false ? "none" : "edit");
  const readOnly = writeMode === "none";
  const proposeOnly = writeMode === "propose";
  const canReview = Boolean(structure?.course.canReview);
  const canUseAssistant = Boolean(capabilities?.useBuilderAssistant);

  const flashSaved = useCallback((id: string) => {
    setSavedFlash((prev) => ({ ...prev, [id]: true }));
    window.setTimeout(() => setSavedFlash((prev) => ({ ...prev, [id]: false })), 1600);
  }, []);

  /**
   * Field edits patch local state and do NOT refetch — a title change on a
   * 244-item course used to re-pull the whole tree. None of these fields feed
   * the completeness counts, so displayed progress stays correct.
   */
  const patchLocal = useCallback(
    (kind: "container" | "item", id: string, changes: Record<string, unknown>) => {
      setStructure((prev) => {
        if (!prev) return prev;
        const mapItems = (items: Item[]) =>
          kind === "item" ? items.map((i) => (i.id === id ? { ...i, ...changes } : i)) : items;
        const walk = (nodes: Container[]): Container[] =>
          nodes.map((node) => {
            const next = kind === "container" && node.id === id ? { ...node, ...changes } : node;
            return { ...next, activities: mapItems(next.activities), children: walk(next.children) };
          });
        return {
          ...prev,
          activities: mapItems(prev.activities),
          containers: walk(prev.containers),
        };
      });
    },
    [],
  );

  /** Optimistic field save: patch locally, persist, roll back on failure. */
  const saveField = useCallback(
    async (
      kind: "container" | "item",
      id: string,
      path: string,
      body: Record<string, unknown>,
      label: string,
    ) => {
      patchLocal(kind, id, body);
      try {
        const result = await patch(path, body);
        const proposed = isProposed(result);
        flashSaved(id);
        notice({
          type: "success",
          title: autoSavedLabel(body, proposed),
          duration: 1400,
          compact: true,
          showCloseButton: false,
          colorBackground: false,
        });
        if (proposed) await load();
      } catch (err) {
        showError(label, err);
        await load(); // put the tree back the way the server sees it
      }
    },
    [patchLocal, flashSaved, showError, load, notice],
  );

  /** Structural changes (add/delete/move/template) do refetch — the shape changed. */
  const mutate = async (label: string, fn: () => Promise<unknown>, successMsg?: string) => {
    setBusy(true);
    try {
      const result = await fn();
      if (isProposed(result)) toast.success("Proposed");
      else if (successMsg) toast.success(successMsg);
      await load();
    } catch (err) {
      showError(label, err);
    } finally {
      setBusy(false);
    }
  };

  const saveContainerField = (containerId: string, body: Record<string, unknown>) =>
    void saveField("container", containerId, `/api/containers/${containerId}`, body, "Could not save the container");
  const saveItemField = (itemId: string, body: Record<string, unknown>) =>
    void saveField("item", itemId, `/api/activities/${itemId}`, body, "Could not save the item");

  const handleAddItemType = (type: string) => {
    const target = typePickerFor;
    setTypePickerFor(null);
    if (!target) return;
    const parent = target.containerId ?? "course";
    const path = target.containerId
      ? `/api/containers/${target.containerId}/activities`
      : `/api/courses/${structure?.course.id}/activities`;
    // Title defaults to the type's own name — never "Untitled".
    const title = ITEM_TYPES.find((t) => t.type === type)?.label ?? type;
    if (target.containerId) {
      setExpanded((prev) => ({ ...prev, [target.containerId!]: true }));
    }
    setCreating(parent);
    void mutate("Could not add the item", async () => {
      const env = await post<{ success: boolean; data?: { id?: string } }>(path, { type, title });
      markCreated(env?.data?.id);
    }).finally(() => setCreating(null));
  };

  /** Add a container. Omit `type` to pick the next tier down from `parent`. */
  const addContainer = (parent?: Container, type?: string) => {
    const tier = type
      ? CONTAINER_TIERS.find((t) => t.type === type)
      : parent
        ? childTierOf(parent.type)
        : CONTAINER_TIERS[0];
    if (!tier) return;
    const word = tier.label.toLowerCase();
    setNamePrompt({
      title: `Add ${word}`,
      label: `${tier.label} name`,
      placeholder:
        tier.type === "unit"
          ? "e.g. Foundations of Western Political Thought"
          : "e.g. Mendel's Laws of Inheritance",
      onSubmit: (name) => {
        if (parent) setExpanded((prev) => ({ ...prev, [parent.id]: true }));
        setCreating(parent?.id ?? "course");
        void mutate(`Could not add the ${word}`, async () => {
          const env = await post<{ success: boolean; data?: { id?: string } }>(
            parent
              ? `/api/containers/${parent.id}/children`
              : `/api/courses/${structure?.course.id}/containers`,
            { type: tier.type, title: name },
          );
          const id = env?.data?.id;
          markCreated(id);
          if (id) setExpanded((prev) => ({ ...prev, [id]: true }));
        }).finally(() => setCreating(null));
      },
    });
  };

  const toggle = (key: string) => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }));

  const setAllExpanded = (open: boolean) => {
    if (!structure) return;
    const next: Record<string, boolean> = {};
    const walk = (nodes: Container[]) => {
      for (const node of nodes) {
        next[node.id] = open;
        walk(node.children);
      }
    };
    walk(structure.containers);
    setExpanded(next);
  };

  const anyExpanded = Object.values(expanded).some(Boolean);

  const togglePublish = (kind: "container" | "activity", id: string, next: boolean, item?: Item) => {
    if (kind === "activity" && next && item && !item.complete) {
      const gaps = outlineItemGaps(item);
      notice({
        type: "warning",
        title: "Finish the item first",
        subtext: gaps.join(" ") || "An item has to be complete before it can be published.",
        duration: 7000,
      });
      return;
    }
    const path =
      kind === "container" ? `/api/containers/${id}/publish` : `/api/activities/${id}/publish`;
    // Publishing cascades to children, so take the server's word for the tree.
    void mutate("Could not change publish state", () => patch(path, { published: next }));
  };

  const movePath = (kind: string, id: string) =>
    kind === "container" ? `/api/containers/${id}/move` : `/api/activities/${id}/move`;

  /**
   * Reorder in place, then persist. Refetching the whole 245-item tree after a
   * drag was costing a second on its own — and we already know the new order,
   * so there is nothing to learn from the server on success.
   */
  const reorder = useCallback(
    (kind: string, parent: string, id: string, toIndex: number) => {
      // Arrows at the ends of a list produce an out-of-range target: no-op.
      if (toIndex < 0) return;

      const move = <T extends { id: string }>(list: T[]) => {
        const from = list.findIndex((x) => x.id === id);
        if (from === -1 || from === toIndex) return list;
        const next = [...list];
        const [row] = next.splice(from, 1);
        next.splice(Math.min(toIndex, next.length), 0, row);
        return next;
      };

      setStructure((prev) => {
        if (!prev) return prev;

        // `parent` is a container id, or "course" for the top of the outline.
        const walk = (nodes: Container[]): Container[] =>
          nodes.map((node) => ({
            ...node,
            activities:
              kind === "item" && node.id === parent ? move(node.activities) : node.activities,
            children:
              kind === "container" && node.id === parent
                ? move(node.children)
                : walk(node.children),
          }));

        if (parent === "course") {
          return kind === "container"
            ? { ...prev, containers: move(prev.containers) }
            : { ...prev, activities: move(prev.activities) };
        }
        return { ...prev, containers: walk(prev.containers) };
      });

      void (async () => {
        try {
          await patch(movePath(kind, id), { toIndex });
        } catch (err) {
          showError("Could not reorder", err);
          await load(); // server disagreed — take its ordering
        }
      })();
    },
    [showError, load],
  );

  /** Drop `drag` onto the row at `toIndex` within the same sibling list. */
  const handleDrop = (kind: string, parent: string, toIndex: number) => {
    const source = drag;
    setDrag(null);
    setDropTarget(null);
    if (!source || source.kind !== kind || source.parent !== parent) return;
    if (kind === "item" && structure) {
      // Dropped onto an item on the other side of a unit's lessons: it moves there too.
      const unit = findContainer(structure.containers, parent);
      if (unit && unit.children.length) {
        const moving = unit.activities.find((row) => row.id === source.id);
        const target = unit.activities[toIndex];
        if (moving && target && unitPlacementOf(moving) !== unitPlacementOf(target)) {
          placeItem(moving, unitPlacementOf(target));
        }
      }
    }
    reorder(kind, parent, source.id, toIndex);
  };

  /**
   * Drag is split in two: only the grip starts a drag, so the inline title stays
   * selectable. The whole row is still a drop zone.
   */
  const dragHandleProps = (kind: string, parent: string, id: string) =>
    readOnly
      ? {}
      : {
          draggable: true,
          onDragStart: (e: DragEvent) => {
            e.stopPropagation();
            e.dataTransfer.effectAllowed = "move";
            setDrag({ kind, parent, id });
          },
          onDragEnd: () => {
            setDrag(null);
            setDropTarget(null);
          },
        };

  const dropZoneProps = (kind: string, parent: string, id: string, index: number) =>
    readOnly
      ? {}
      : {
          onDragOver: (e: DragEvent) => {
            if (!drag || drag.kind !== kind || drag.parent !== parent) return;
            e.preventDefault();
            e.stopPropagation();
            e.dataTransfer.dropEffect = "move";
            if (dropTarget !== id) setDropTarget(id);
          },
          onDragLeave: () => {
            if (dropTarget === id) setDropTarget(null);
          },
          onDrop: (e: DragEvent) => {
            e.preventDefault();
            e.stopPropagation();
            handleDrop(kind, parent, index);
          },
          className: dropTarget === id && drag?.id !== id ? "al-cb-drop-target" : undefined,
        };

  const tourUnitId = structure ? firstUnit(structure)?.id : undefined;
  const tourLessonId = structure ? firstLesson(structure)?.id : undefined;
  const tourSeenTypes = new Set<string>();

  /** Save where a unit item sits relative to the unit's lessons. Other settings are kept. */
  const placeItem = (item: Item, next: UnitPlacement) =>
    saveItemField(item.id, { metadata: { ...(item.metadata || {}), unitPlacement: next } });

  const renderItemRow = (
    item: Item,
    parent: string,
    index: number,
    group?: { prevIndex: number; nextIndex: number; placement: UnitPlacement },
  ) => {
    const Icon = iconFor(item.type);
    const tourEdit = !tourSeenTypes.has(item.type);
    if (tourEdit) tourSeenTypes.add(item.type);
    const dz = isProposedNode(item) ? {} : dropZoneProps("item", parent, item.id, index);
    return (
      <div
        key={item.id}
        data-outline-id={item.id}
        {...dz}
        className={`al-cb-item${parent === "course" ? " al-cb-item--top" : ""}${isProposedNode(item) ? " al-cb-proposed" : ""}${dz.className ? ` ${dz.className}` : ""}${justCreated[item.id] ? " al-cb-enter" : ""}`}
        title={isProposedNode(item) ? "Proposed — only you and reviewers see this" : undefined}
      >
        {!readOnly && !isProposedNode(item) ? (
          <span
            className="al-cb-grip"
            title="Drag to reorder"
            {...dragHandleProps("item", parent, item.id)}
          >
            <GripVertical size={13} />
          </span>
        ) : null}
        <span className="al-cb-item-icon"><Icon size={15} /></span>
        <InlineTitle
          value={item.title}
          placeholder={`Untitled ${item.label}`}
          disabled={readOnly || busy}
          saved={savedFlash[item.id]}
          onSave={(title) => saveItemField(item.id, { title })}
        />
        <span className="al-cb-badge">{item.label}</span>
        {isProposedNode(item) ? <span className="al-cb-badge al-cb-badge--proposed">Proposed</span> : null}
        {item.dueAt ? (
          <span
            className={`al-cb-due${new Date(item.dueAt) < new Date() ? " al-cb-due--past" : ""}`}
            title={new Date(item.dueAt).toLocaleString()}
          >
            Due {formatDue(item.dueAt)}
          </span>
        ) : null}
        {Number(item.totalPoints) > 0 ? (
          <span className="al-cb-points">{Number(item.totalPoints)} pts</span>
        ) : null}
        {Number(item.estimatedMinutes) > 0 ? (
          <span className="al-cb-minutes">{Number(item.estimatedMinutes)}m</span>
        ) : null}
        <span className={item.complete ? "al-cb-badge al-cb-badge--done" : "al-cb-badge al-cb-badge--todo"}>
          {item.complete ? "Complete" : "Incomplete"}
        </span>
        {isProposedNode(item) ? null : (
          <PublishToggle
            published={item.published}
            disabled={readOnly || busy || (!item.published && !item.complete)}
            label={item.title || item.label}
            title={
              !item.published && !item.complete
                ? `Finish the item before publishing. ${outlineItemGaps(item).join(" ")}`.trim()
                : undefined
            }
            onToggle={() => togglePublish("activity", item.id, !item.published, item)}
          />
        )}
        <button
          type="button"
          className="al-cb-edit-btn"
          data-tour={tourEdit ? `cb-edit-${item.type}` : undefined}
          onClick={() => setEditingItemId(item.id)}
        >
          <Pencil size={12} aria-hidden="true" />
          Edit
        </button>
        {!readOnly ? (
          <RowMenu
            disabled={busy}
            label={item.title || item.label}
            hideReorder={isProposedNode(item)}
            onMoveUp={() => reorder("item", parent, item.id, group ? group.prevIndex : index - 1)}
            onMoveDown={() =>
              reorder("item", parent, item.id, group ? (group.nextIndex < 0 ? -1 : group.nextIndex) : index + 1)
            }
            placement={group?.placement ?? null}
            onPlace={(next) => placeItem(item, next)}
            onDelete={() =>
              setPendingDelete({
                kind: "item",
                id: item.id,
                title: `Remove “${item.title || `Untitled ${item.label}`}”?`,
                message: "This item is removed from the course.",
                errorLabel: "Could not remove the item",
                successLabel: "Item removed",
              })
            }
          />
        ) : null}
      </div>
    );
  };

  /**
   * One renderer for every tier. `depth` picks the existing unit/lesson styling
   * so the outline looks exactly as it did; everything else — fields, actions,
   * nesting — is the same at every level.
   */
  const renderContainer = (node: Container, parent: string, index: number, depth: number) => {
    const open = expanded[node.id];
    const dz = isProposedNode(node) ? {} : dropZoneProps("container", parent, node.id, index);
    const tier = depth === 0 ? "unit" : "lesson";
    const childTier = childTierOf(node.type);
    const word = node.typeLabel.toLowerCase();
    const fields = tierSpec(node.type)?.fields || {};

    return (
      <article
        className={`al-cb-${tier}${open ? ` al-cb-${tier}--open` : ""}${isProposedNode(node) ? " al-cb-proposed" : ""}${justCreated[node.id] ? " al-cb-enter" : ""}`}
        key={node.id}
        data-outline-id={node.id}
      >
        <div
          {...dz}
          data-tour={node.id === tourUnitId ? "cb-unit" : node.id === tourLessonId ? "cb-lesson" : undefined}
          className={`al-cb-${tier}-row${isProposedNode(node) ? " al-cb-proposed" : ""}${dz.className ? ` ${dz.className}` : ""}`}
          title={isProposedNode(node) ? "Proposed — only you and reviewers see this" : undefined}
          onClick={(e) => {
            const target = e.target as HTMLElement;
            if (target.closest(".al-cb-grip, .al-cb-publish, .al-cb-menu")) return;
            toggle(node.id);
          }}
        >
          {!readOnly && !isProposedNode(node) ? (
            <span
              className="al-cb-grip"
              title="Drag to reorder"
              {...dragHandleProps("container", parent, node.id)}
              onClick={(e) => e.stopPropagation()}
            >
              <GripVertical size={depth === 0 ? 14 : 13} />
            </span>
          ) : null}
          <button
            type="button"
            className="al-cb-caret"
            onClick={(e) => {
              e.stopPropagation();
              toggle(node.id);
            }}
            aria-label={`${open ? "Collapse" : "Expand"} ${word}`}
          >
            {open ? (
              <ChevronDown size={depth === 0 ? 15 : 14} />
            ) : (
              <ChevronRight size={depth === 0 ? 15 : 14} />
            )}
          </button>
          <KindBadge
            mark={markForContainer(node.type)}
            label={node.typeLabel}
            onIdleClick={() => toggle(node.id)}
          />
          <InlineTitle
            value={node.title}
            placeholder={`Untitled ${node.typeLabel}`}
            disabled={readOnly || busy}
            className={`al-cb-inline-input al-cb-inline-input--${tier}`}
            saved={savedFlash[node.id]}
            editOnDoubleClick
            tour={node.id === tourUnitId ? "cb-unit-title" : node.id === tourLessonId ? "cb-lesson-title" : undefined}
            onIdleClick={() => toggle(node.id)}
            onSave={(title) => saveContainerField(node.id, { title })}
          />
          <ProgressPill progress={node.progress} />
          {isProposedNode(node) ? <span className="al-cb-badge al-cb-badge--proposed">Proposed</span> : (
            <PublishToggle
              published={node.published}
              disabled={readOnly || busy}
              label={node.title || node.label}
              onToggle={() => togglePublish("container", node.id, !node.published)}
            />
          )}
          {!readOnly ? (
            <RowMenu
              disabled={busy}
              label={node.title || node.label}
              hideReorder={isProposedNode(node)}
              onMoveUp={() => reorder("container", parent, node.id, index - 1)}
              onMoveDown={() => reorder("container", parent, node.id, index + 1)}
              onDelete={() =>
                setPendingDelete({
                  kind: "container",
                  id: node.id,
                  title: `Remove ${node.label}${node.title ? ` — “${node.title}”` : ""}?`,
                  message: `Everything inside this ${word} is removed with it.`,
                  errorLabel: `Could not remove the ${word}`,
                  successLabel: `${node.typeLabel} removed`,
                })
              }
            />
          ) : null}
        </div>

        {open ? (
          <div className={`al-cb-${tier}-body`}>
            {fields.objectives ? (
              <>
                <label className="al-cb-label">
                  {fields.objectives.label}
                  {fields.objectives.hint ? ` · ${fields.objectives.hint}` : ""}
                </label>
                <InlineTextarea
                  value={node.objectives.join("\n")}
                  placeholder={
                    fields.coreQuestion
                      ? "e.g. Analyze the causes of the Industrial Revolution\nEvaluate its impact on labor and cities"
                      : `1–2 measurable objectives students will meet in this ${word}`
                  }
                  disabled={readOnly || busy}
                  onSave={(text) =>
                    saveContainerField(node.id, {
                      objectives: text.split("\n").map((s) => s.trim()).filter(Boolean),
                    })
                  }
                />
              </>
            ) : null}
            {fields.coreQuestion || fields.standardCodes ? (
              <div className="al-cb-field-row">
                {fields.coreQuestion ? (
                  <div>
                    <label className="al-cb-label">{fields.coreQuestion.label}</label>
                    <InlineTitle
                      value={node.coreQuestion}
                      placeholder={`The ${word}'s central question`}
                      disabled={readOnly || busy}
                      className="al-cb-input"
                      onSave={(coreQuestion) => saveContainerField(node.id, { coreQuestion })}
                    />
                  </div>
                ) : null}
                {fields.standardCodes ? (
                  <ContainerStandardsField
                    label={fields.standardCodes.label}
                    selectedIds={node.taxonomyIds}
                    options={standardOptions}
                    scopeOptions={taxonomyOptions}
                    loading={taxonomyLoading}
                    scopeId={structure?.course.primaryTaxonomy || ""}
                    scopeTitle={
                      taxonomyOptions.find((row) => row.id === structure?.course.primaryTaxonomy)
                        ?.title || null
                    }
                    disabled={readOnly || busy}
                    saved={savedFlash[node.id]}
                    onSave={(taxonomyIds) => saveContainerField(node.id, { taxonomyIds })}
                    onOptionsChange={setStandardOptions}
                  />
                ) : null}
              </div>
            ) : null}

            {node.children.length ? (
              (() => {
                // A unit: its own items sit above or below its lessons, as the
                // teacher placed them. Students see the same order.
                const rows = node.activities.map((item, i) => ({ item, i }));
                const before = rows.filter((row) => unitPlacementOf(row.item) === "before");
                const after = rows.filter((row) => unitPlacementOf(row.item) === "after");
                const renderGroup = (group: typeof rows, placement: UnitPlacement) =>
                  group.map((row, g) =>
                    renderItemRow(row.item, node.id, row.i, {
                      prevIndex: g > 0 ? group[g - 1].i : -1,
                      nextIndex: g < group.length - 1 ? group[g + 1].i : -1,
                      placement,
                    }),
                  );
                return (
                  <>
                    {renderGroup(before, "before")}
                    {node.children.map((child, i) => renderContainer(child, node.id, i, depth + 1))}
                    {after.length ? <p className="al-cb-after-lessons">After the lessons</p> : null}
                    {renderGroup(after, "after")}
                  </>
                );
              })()
            ) : (
              node.activities.map((item, i) => renderItemRow(item, node.id, i))
            )}
            {creating === node.id ? <CreatingGhost /> : null}

            {!readOnly ? (
              <div className="al-cb-add-row">
                {childTier ? (
                  <AddToParentButton
                    disabled={busy}
                    parentWord={word}
                    containerTypes={CONTAINER_TIERS.filter((tier) => tier.type !== "unit").map((tier) => tier.type)}
                    trigger="link"
                    tourButton={node.id === tourUnitId ? "cb-add-unit" : undefined}
                    forceOpen={walkPhase === "tour" && walkCurrent?.openAdd === "unit" && node.id === tourUnitId}
                    onPickContainer={(type) => addContainer(node, type)}
                    onPickItem={() => setTypePickerFor({ containerId: node.id })}
                  />
                ) : (
                  <button
                    type="button"
                    className="al-cb-add-link"
                    data-tour={node.id === tourLessonId ? "cb-add-lesson" : undefined}
                    disabled={busy}
                    onClick={() => setTypePickerFor({ containerId: node.id })}
                  >
                    <Plus size={13} aria-hidden="true" />
                    Add item to {word}
                  </button>
                )}
              </div>
            ) : null}
          </div>
        ) : null}
      </article>
    );
  };

  return (
    <div className="al-cb">
      <LoadingOverlay isVisible={loading} message="Loading course…" />

      <Link to="/courses" className="al-cb-back">
        <ArrowLeft size={14} aria-hidden="true" />
        Courses
      </Link>

      {loading ? null : !structure ? (
        <p className="al-cb-muted">Course not found.</p>
      ) : (
        <>
          <header className="al-cb-header">
            <div>
              <h1>{structure.course.title}</h1>
              <p className="al-cb-sub">
                Add units, lessons, or items in any shape — a lesson can sit on the
                course itself — or start from the standard structure and adjust.
              </p>
            </div>
            <div className="al-cb-header-meta">
              {!readOnly ? (
                <button
                  type="button"
                  className={`al-cb-btn al-cb-btn--ghost${walkPhase === "tour" ? " al-cb-walk-exit" : ""}`}
                  onClick={() => {
                    if (walkPhase === "tour") {
                      rememberWalkthrough(user?.id, "dismissed");
                      setWalkPhase("idle");
                      return;
                    }
                    setWalkStep(0);
                    setWalkPhase("offer");
                  }}
                >
                  <CircleHelp size={14} aria-hidden />
                  {walkPhase === "tour" ? "End walkthrough" : "Walkthrough"}
                </button>
              ) : null}
              <button
                type="button"
                className="al-cb-btn al-cb-btn--ghost"
                onClick={() => setCoursePreviewOpen(true)}
              >
                <Eye size={14} aria-hidden />
                Preview course
              </button>
              {showBreaker ? (
                <button
                  type="button"
                  className="al-cb-btn al-cb-btn--ghost al-cb-breaker-btn"
                  onClick={() => setBreakerOpen(true)}
                >
                  <Trash2 size={14} aria-hidden />
                  Break course
                </button>
              ) : null}
              {structure.course.ownerSchoolId && capabilities?.postAnnouncements ? (
                <button
                  type="button"
                  className="al-cb-btn al-cb-btn--ghost"
                  onClick={() => setAnnounceOpen(true)}
                >
                  <Megaphone size={14} aria-hidden />
                  Announce
                </button>
              ) : null}
              <ProgressPill progress={structure.progress} />
              {readOnly ? <span className="al-cb-readonly">Read only</span> : null}
              {proposeOnly ? <span className="al-cb-readonly">Propose only</span> : null}
            </div>
          </header>

          {proposeOnly ? (
            <p className="al-cb-banner">
              This copy is propose-only. Saves become proposals
              {canReview ? " — you can also apply or reject pending ones." : " for a reviewer to apply."}
            </p>
          ) : null}
          {structure.course.modLock && writeMode === "none" ? (
            <p className="al-cb-banner">This course is mod-locked.</p>
          ) : null}
          {canReview && writeMode === "none" && !structure.course.modLock ? (
            <p className="al-cb-banner">You can review proposals on this copy. You cannot live-edit it.</p>
          ) : null}
          {canReview && (structure.course.pendingProposals || 0) > 0 ? (
            <ProposalsPanel courseId={structure.course.id} structure={structure} onChanged={load} />
          ) : null}

          <CourseHeaderCard
            structure={structure}
            readOnly={readOnly}
            proposeOnly={proposeOnly}
            forceDetailsOpen={walkPhase === "tour"}
            onSaved={load}
            showError={showError}
          />

          <section className="al-cb-outline-section" data-tour="cb-outline">
            <div className="al-cb-outline-head">
              <h2>Outline</h2>
              <div className="al-cb-outline-head-actions">
                {structure.containers.length > 0 ? (
                  <button
                    type="button"
                    className="al-cb-btn al-cb-btn--ghost"
                    onClick={() => setAllExpanded(!anyExpanded)}
                  >
                    {anyExpanded ? <ChevronsDownUp size={14} /> : <ChevronsUpDown size={14} />}
                    {anyExpanded ? "Collapse all" : "Expand all"}
                  </button>
                ) : null}
              </div>
              {!readOnly ? (
                <div className="al-cb-outline-head-actions">
                  <button
                    type="button"
                    className="al-cb-btn al-cb-btn--ghost"
                    disabled={busy || !structure.empty}
                    title={structure.empty ? "Fill this course with the standard structure" : "Available only while the course is empty"}
                    onClick={() =>
                      void mutate(
                        "Could not apply the template",
                        () => post(`/api/courses/${structure.course.id}/template`, {}),
                        "Standard structure added",
                      )
                    }
                  >
                    <LayoutTemplate size={14} aria-hidden="true" />
                    Use standard template
                  </button>
                </div>
              ) : null}
            </div>

            {structure.containers.length === 0 && structure.activities.length === 0 && creating !== "course" ? (
              <div className="al-cb-empty">
                <LayoutTemplate size={26} aria-hidden="true" />
                <h3>Nothing here yet</h3>
                <p>
                  Start from the standard course structure — 9 units, 4 lessons each, 6 components
                  per lesson — or add a unit, a lesson, or an item and build freely.
                </p>
              </div>
            ) : null}

            {structure.containers.map((node, i) => renderContainer(node, "course", i, 0))}

            {structure.activities.map((item, i) => renderItemRow(item, "course", i))}

            {!readOnly ? (
              <div className="al-cb-outline-foot">
                {creating === "course" ? <CreatingGhost /> : null}
                <AddToParentButton
                  disabled={busy}
                  parentWord="course"
                  containerTypes={CONTAINER_TIERS.map((tier) => tier.type)}
                  tourButton="cb-add-course"
                  forceOpen={walkPhase === "tour" && walkCurrent?.openAdd === "course"}
                  onPickContainer={(type) => addContainer(undefined, type)}
                  onPickItem={() => setTypePickerFor({})}
                />
              </div>
            ) : null}
          </section>

          {/* Name a unit or lesson before it exists */}
          <NamePromptDialog prompt={namePrompt} onClose={() => setNamePrompt(null)} />

          <CoursePreviewDialog
            open={coursePreviewOpen}
            structure={structure}
            onClose={() => setCoursePreviewOpen(false)}
          />

          {showBreaker ? (
            <CourseBreakerDialog
              open={breakerOpen}
              tree={breakerTree}
              canDeleteCourse={Boolean(
                structure?.course?.isMaster && !structure?.course?.ownerSchoolId
                  ? capabilities?.removeMasterCourses
                  : capabilities?.removeCourses,
              )}
              onClose={() => setBreakerOpen(false)}
              onRemoved={(removedCourse) => {
                setBreakerOpen(false);
                if (removedCourse) {
                  toast.success("Course deleted");
                  navigate("/courses");
                  return;
                }
                toast.success("Selected outline removed");
                void load();
              }}
            />
          ) : null}

          <NotificationModal
            isOpen={Boolean(pendingDelete)}
            type="warning"
            icon={<Trash2 />}
            title={pendingDelete?.title ?? "Remove"}
            message={pendingDelete?.message}
            overlay
            overlayClose={!busy}
            escapeClose={!busy}
            showOkButton={false}
            align="center"
            iconSize="2.25rem"
            titleSize="1.05rem"
            style={COURSE_DIALOG_STYLE}
            onClose={() => {
              if (!busy) setPendingDelete(null);
            }}
          >
            <div className="al-courses-assign-actions">
              <button
                type="button"
                className="al-cb-btn al-cb-btn--ghost"
                disabled={busy}
                onClick={() => setPendingDelete(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="al-cb-btn al-cb-btn--danger"
                disabled={busy}
                onClick={() => {
                  const pending = pendingDelete;
                  if (!pending) return;
                  setPendingDelete(null);
                  const path =
                    pending.kind === "item"
                      ? `/api/activities/${pending.id}`
                      : `/api/containers/${pending.id}`;
                  void mutate(pending.errorLabel, () => del(path), pending.successLabel);
                }}
              >
                Remove
              </button>
            </div>
          </NotificationModal>

          {/* Item type picker */}
          <NotificationModal
            isOpen={typePickerFor != null}
            type="neutral"
            icon={<FileText />}
            title="Select item type"
            message="What kind of item should this be?"
            overlay
            overlayClose
            showOkButton={false}
            align="center"
            iconSize="2.25rem"
            titleSize="1.05rem"
            style={{ ...COURSE_DIALOG_STYLE, maxHeight: "min(84vh, 40rem)" }}
            onClose={() => setTypePickerFor(null)}
          >
            {TYPE_GROUPS.map((group) => (
              <div key={group.label} className="al-cb-type-group">
                <p className="al-cb-type-group-label">{group.label}</p>
                <div className="al-cb-type-grid">
                  {group.types.map((type) => {
                    const spec = ITEM_TYPES.find((t) => t.type === type);
                    if (!spec) return null;
                    const Icon = spec.icon;
                    return (
                      <button
                        key={spec.type}
                        type="button"
                        className="al-cb-choice"
                        data-tour={`cb-type-${spec.type}`}
                        onClick={() => handleAddItemType(spec.type)}
                      >
                        <span className="al-cb-choice-icon"><Icon size={17} /></span>
                        <span>
                          <span className="al-cb-choice-title">{spec.label}</span>
                          <span className="al-cb-choice-desc">{spec.description}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </NotificationModal>

          {/* Item editor */}
          {editingItem ? (
            <ItemEditorDialog
              key={editingItem.id}
              item={editingItem}
              structure={structure}
              coursePrimaryTaxonomy={structure.course.primaryTaxonomy}
              readOnly={readOnly}
              proposeOnly={proposeOnly}
              tourPanel={walkPhase === "tour" ? walkCurrent?.editorPanel ?? null : null}
              onClose={() => setEditingItemId(null)}
              onSaved={load}
              showError={showError}
              toastSuccess={(m) => toast.success(m)}
            />
          ) : null}
        </>
      )}
    {structure && canUseAssistant ? (
      <CourseAssistant
        courseId={structure.course.id}
        canWrite={!readOnly}
        rememberHistory={Boolean(capabilities?.builderAssistantHistory)}
        focusItemId={editingItemId}
        open={assistantOpen}
        onOpen={() => setAssistantOpen(true)}
        onClose={() => setAssistantOpen(false)}
        onCourseChanged={load}
      />
    ) : null}
    <CourseAnnounceDialog
      target={
        announceOpen && structure?.course.ownerSchoolId
          ? { courseId: structure.course.id, title: structure.course.title }
          : null
      }
      onClose={() => setAnnounceOpen(false)}
    />
    {walkPhase !== "idle" ? (
      <CourseWalkthrough
        mode={walkPhase}
        steps={walkSteps}
        stepIndex={walkStep}
        chapter={walkChapterAt(walkChapters, walkStep)?.title ?? "Course details"}
        outline={walkOutline(walkChapters)}
        onJump={(index) => setWalkStep(index)}
        onStart={() => {
          setWalkStep(0);
          setWalkPhase("tour");
        }}
        onDismiss={() => {
          rememberWalkthrough(user?.id, "dismissed");
          setWalkPhase("idle");
        }}
        onClose={() => {
          rememberWalkthrough(user?.id, "dismissed");
          setWalkPhase("idle");
        }}
        onBack={() => setWalkStep((current) => Math.max(0, current - 1))}
        onNext={() => {
          if (walkStep >= walkSteps.length - 1) {
            rememberWalkthrough(user?.id, "done");
            setWalkPhase("idle");
            setWalkStep(0);
            return;
          }
          setWalkStep((current) => current + 1);
        }}
      />
    ) : null}
    </div>
  );
}

function HintButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <button type="button" className="al-cb-icon-btn" aria-label={label} title={label} onClick={onClick}>
      <CircleHelp size={15} aria-hidden="true" />
    </button>
  );
}

function HintDialog({
  title,
  onClose,
  children,
}: {
  title: string;
  onClose: () => void;
  children: ReactNode;
}) {
  return (
    <Dialog open title={title} width="26rem" onClose={onClose}>
      <div className="al-cb-hint-copy">{children}</div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Course header card
// ---------------------------------------------------------------------------

const PDF_ACCEPT = ".pdf,application/pdf";

function isPdfMedia(file: { mimeType?: string; originalFilename?: string; name?: string }) {
  const mime = String(file.mimeType || "").toLowerCase();
  const name = String(file.originalFilename || file.name || "").toLowerCase();
  return mime === "application/pdf" || name.endsWith(".pdf");
}

function CourseHeaderCard({
  structure,
  readOnly,
  proposeOnly,
  forceDetailsOpen = false,
  onSaved,
  showError,
}: {
  structure: Structure;
  readOnly: boolean;
  proposeOnly: boolean;
  forceDetailsOpen?: boolean;
  onSaved: () => Promise<void>;
  showError: (title: string, err: unknown) => void;
}) {
  const { toast } = useNotification();
  const { pageAccess } = useAuth();
  const canManageRubrics = Boolean(pageAccess?.["grade-rubrics"]);
  const course = structure.course;
  const [title, setTitle] = useState(course.title);
  const [description, setDescription] = useState(course.description || "");
  const [taxonomyOptions, setTaxonomyOptions] = useState<TaxonomyOption[]>([]);
  const [taxonomyLoading, setTaxonomyLoading] = useState(true);
  const [taxonomyId, setTaxonomyId] = useState(course.primaryTaxonomy || "");
  const [levels, setLevels] = useState<string[]>(course.gradeLevels);
  const [rubricId, setRubricId] = useState(course.gradeRubric?.id || "");
  const [rubricOpen, setRubricOpen] = useState(false);
  const [rubricOptions, setRubricOptions] = useState<NonNullable<Structure["course"]["gradeRubric"]>[]>(
    course.gradeRubric ? [course.gradeRubric] : [],
  );
  const [objectives, setObjectives] = useState((course.objectives || []).join("\n"));
  const [syllabus, setSyllabus] = useState<{ mediaId: string; url: string; name: string } | null>(
    course.syllabus?.mediaId && course.syllabus.url
      ? {
          mediaId: course.syllabus.mediaId,
          url: course.syllabus.url,
          name: course.syllabus.name || "Syllabus.pdf",
        }
      : null,
  );
  const [syllabusBusy, setSyllabusBusy] = useState(false);
  const [syllabusPickerOpen, setSyllabusPickerOpen] = useState(false);
  const [syllabusFiles, setSyllabusFiles] = useState<LearnMedia[] | null>(null);
  const syllabusFileRef = useRef<HTMLInputElement | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveShown, setSaveShown] = useState(false);
  const [saveLeaving, setSaveLeaving] = useState(false);
  const [saveMotion, setSaveMotion] = useState<"in" | "idle" | "bump">("in");
  const saveLeaveTimer = useRef<number | null>(null);
  const saveBumpTimer = useRef<number | null>(null);
  const saveSeenChange = useRef(false);
  const [open, setOpen] = useState(false);
  const [taxonomyListOpen, setTaxonomyListOpen] = useState(false);

  useEffect(() => {
    if (forceDetailsOpen) setOpen(true);
  }, [forceDetailsOpen]);

  useEffect(() => {
    setRubricId(course.gradeRubric?.id || "");
  }, [course.gradeRubric?.id]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const envelope = await api<{ success: boolean; data: TaxonomyOption[] }>("/api/courses/taxonomy");
        if (cancelled) return;
        setTaxonomyOptions(Array.isArray(envelope.data) ? envelope.data : []);
      } catch {
        /* header still works without taxonomy */
      } finally {
        if (!cancelled) setTaxonomyLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [course.primaryTaxonomy]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const envelope = await api<{ success: boolean; data: NonNullable<Structure["course"]["gradeRubric"]>[] }>(
          "/api/grade-rubrics",
        );
        if (cancelled) return;
        const rows = Array.isArray(envelope.data) ? envelope.data.filter(Boolean) : [];
        setRubricOptions(rows);
      } catch {
        /* the current rubric still shows from the course */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [course.id]);

  // Hours roll-up from item estimated minutes, like the original's header.
  const hours = useMemo(() => {
    const minutes: Record<string, number> = {};
    let all = 0;
    const collect = (items: Item[]) => {
      for (const item of items) {
        const m = Number(item.estimatedMinutes) || 0;
        if (m) all += m;
        const primarySplit = item.minuteTypeSplits?.primary;
        if (primarySplit && typeof primarySplit === "object") {
          for (const bucket of HOUR_BUCKETS) {
            const n = Number(primarySplit[bucket.value]) || 0;
            if (!n) continue;
            minutes[bucket.value] = (minutes[bucket.value] || 0) + n;
          }
        }
      }
    };
    const walk = (nodes: Container[]) => {
      for (const node of nodes) {
        collect(node.activities);
        walk(node.children);
      }
    };
    collect(structure.activities);
    walk(structure.containers);
    const h = (m: number) => (m ? `${Math.round((m / 60) * 10) / 10}h` : "0h");
    return { all: h(all), byBucket: HOUR_BUCKETS.map((b) => ({ ...b, value: h(minutes[b.value] || 0) })) };
  }, [structure]);

  const detailsSnapshot = JSON.stringify({
    title,
    description,
    taxonomyId,
    levels: [...levels].sort(),
    rubricId,
    objectives,
    syllabusId: syllabus?.mediaId || "",
  });
  const savedSnapshot = JSON.stringify({
    title: course.title,
    description: course.description || "",
    taxonomyId: course.primaryTaxonomy || "",
    levels: [...(course.gradeLevels || [])].sort(),
    rubricId: course.gradeRubric?.id || "",
    objectives: (course.objectives || []).join("\n"),
    syllabusId: course.syllabus?.mediaId || "",
  });
  const detailsDirty = detailsSnapshot !== savedSnapshot;

  useEffect(() => {
    if (readOnly) return undefined;
    if (detailsDirty || saving) {
      if (saveLeaveTimer.current != null) {
        window.clearTimeout(saveLeaveTimer.current);
        saveLeaveTimer.current = null;
      }
      setSaveLeaving(false);
      setSaveShown(true);
      if (!saveShown) setSaveMotion("in");
      return undefined;
    }
    if (!saveShown) return undefined;
    setSaveLeaving(true);
    saveLeaveTimer.current = window.setTimeout(() => {
      setSaveShown(false);
      setSaveLeaving(false);
      saveLeaveTimer.current = null;
      saveSeenChange.current = false;
    }, 280);
    return () => {
      if (saveLeaveTimer.current != null) {
        window.clearTimeout(saveLeaveTimer.current);
        saveLeaveTimer.current = null;
      }
    };
  }, [detailsDirty, saving, saveShown, readOnly]);

  useEffect(() => {
    if (!saveShown || saveLeaving || !detailsDirty) return undefined;
    if (!saveSeenChange.current) {
      saveSeenChange.current = true;
      return undefined;
    }
    setSaveMotion("idle");
    saveBumpTimer.current = window.setTimeout(() => setSaveMotion("bump"), 20);
    return () => {
      if (saveBumpTimer.current != null) {
        window.clearTimeout(saveBumpTimer.current);
        saveBumpTimer.current = null;
      }
    };
  }, [detailsSnapshot, saveShown, saveLeaving, detailsDirty]);

  const chosenRubric =
    rubricOptions.find((row) => row.id === rubricId) ||
    (course.gradeRubric && course.gradeRubric.id === rubricId ? course.gradeRubric : null);
  const savedRubricId = course.gradeRubric?.id || "";
  const rubricLocked = course.hasActivityProgress === true && Boolean(savedRubricId);
  const canSwitchRubric = !readOnly && !rubricLocked && !saving;

  const save = async () => {
    setSaving(true);
    try {
      const result = await patch(`/api/courses/${course.id}`, {
        title,
        description: description || null,
        primaryTaxonomy: taxonomyId || null,
        gradeLevels: levels,
        gradeRubricId: rubricId || null,
        objectives: objectives
          .split("\n")
          .map((line) => line.trim())
          .filter(Boolean),
        syllabusMediaId: syllabus?.mediaId || null,
      });
      if (isProposed(result)) toast.success("Proposed");
      await onSaved();
    } catch (err) {
      showError("Could not save the course details", err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="al-cb-card">
      <button
        type="button"
        className="al-cb-card-head"
        data-tour="cb-details"
        onClick={() =>
          setOpen((current) => {
            if (forceDetailsOpen) return true;
            const next = !current;
            if (!next) setTaxonomyListOpen(false);
            return next;
          })
        }
      >
        <span>
          <h2>Course details</h2>
          {open || !taxonomyId ? (
            <span className="al-cb-card-sub">Title · syllabus · objectives · grades · rubric</span>
          ) : null}
        </span>
        {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>

      {open ? (
        <div className="al-cb-card-body">
          <CourseMetaFields
            title={title}
            description={description}
            taxonomyId={taxonomyId}
            taxonomyOptions={taxonomyOptions}
            taxonomyLoading={taxonomyLoading}
            saving={saving || readOnly}
            titleId="cb-course-title"
            descriptionId="cb-course-desc"
            onTitle={setTitle}
            onDescription={setDescription}
            onTaxonomy={setTaxonomyId}
            onTaxonomyOptions={setTaxonomyOptions}
            taxonomyListOpen={taxonomyListOpen}
            onTaxonomyListOpenChange={setTaxonomyListOpen}
            autoFocusTitle={!forceDetailsOpen}
          />

          <div data-tour="cb-syllabus">
          <span className="al-cb-label">Syllabus · PDF only · students open this on the Syllabus tab</span>
          <div className="al-cb-syllabus">
            {syllabus ? (
              <p className="al-cb-file-name" title={syllabus.name}>
                <Paperclip size={14} aria-hidden /> {syllabus.name}
              </p>
            ) : (
              <p className="al-cb-muted" style={{ margin: 0 }}>
                No syllabus yet
              </p>
            )}
            {!readOnly ? (
              <div className="al-cb-syllabus-actions">
                <button
                  type="button"
                  className="al-cb-icon-btn"
                  title="Upload a PDF"
                  aria-label="Upload a syllabus PDF"
                  disabled={saving || syllabusBusy}
                  onClick={() => syllabusFileRef.current?.click()}
                >
                  {syllabusBusy ? <Loader2 size={13} className="al-cb-spin" /> : <Upload size={13} />}
                </button>
                <button
                  type="button"
                  className={`al-cb-icon-btn${syllabusPickerOpen ? " al-cb-icon-btn--on" : ""}`}
                  title="My files"
                  aria-label="Choose a syllabus PDF"
                  disabled={saving || syllabusBusy}
                  onClick={() => {
                    setSyllabusPickerOpen(true);
                    setSyllabusFiles(null);
                    void listMyMedia("file")
                      .then((rows) => setSyllabusFiles(rows.filter(isPdfMedia)))
                      .catch((err) => {
                        showError("Could not load your files", err);
                        setSyllabusPickerOpen(false);
                      });
                  }}
                >
                  <FolderOpen size={13} />
                </button>
                {syllabus ? (
                  <button
                    type="button"
                    className="al-cb-icon-btn"
                    title="Remove syllabus"
                    aria-label="Remove syllabus"
                    disabled={saving || syllabusBusy}
                    onClick={() => setSyllabus(null)}
                  >
                    <Trash2 size={13} />
                  </button>
                ) : null}
              </div>
            ) : null}
          </div>
          </div>
          <input
            ref={syllabusFileRef}
            type="file"
            accept={PDF_ACCEPT}
            hidden
            disabled={readOnly || saving || syllabusBusy}
            onChange={(event) => {
              const file = event.target.files?.[0];
              event.target.value = "";
              if (!file || readOnly) return;
              if (!isPdfMedia({ mimeType: file.type, name: file.name })) {
                toast.error("The syllabus has to be a PDF");
                return;
              }
              setSyllabusBusy(true);
              void uploadLearnMedia(file, "file")
                .then((row) => {
                  if (!isPdfMedia(row)) {
                    toast.error("The syllabus has to be a PDF");
                    return;
                  }
                  setSyllabus({
                    mediaId: row.id,
                    url: row.url,
                    name: row.originalFilename || file.name || "Syllabus.pdf",
                  });
                })
                .catch((err) => {
                  showError("Could not upload the syllabus", err);
                })
                .finally(() => setSyllabusBusy(false));
            }}
          />

          <div data-tour="cb-objectives">
          <label className="al-cb-label" htmlFor="cb-course-objectives">
            Course objectives · one per line · students see these on Path
          </label>
          <textarea
            id="cb-course-objectives"
            className="al-cb-textarea al-cb-textarea--tall"
            rows={4}
            value={objectives}
            disabled={readOnly || saving}
            placeholder={"e.g. Explain the causes of the Industrial Revolution\nEvaluate its impact on labor and cities"}
            onChange={(e) => setObjectives(e.target.value)}
          />
          </div>

          <div data-tour="cb-grades">
          <span className="al-cb-label">Grade levels</span>
          <div className="al-cb-grades">
            {!readOnly ? (
              <>
                <button
                  type="button"
                  className={`al-cb-grade al-cb-grade--range${
                    levels.length === GRADE_LEVELS.length ? " al-cb-grade--on" : ""
                  }`}
                  disabled={saving}
                  aria-pressed={levels.length === GRADE_LEVELS.length}
                  onClick={() => setLevels([...GRADE_LEVELS])}
                >
                  All
                </button>
                <button
                  type="button"
                  className={`al-cb-grade al-cb-grade--range${levels.length === 0 ? " al-cb-grade--on" : ""}`}
                  disabled={saving}
                  aria-pressed={levels.length === 0}
                  onClick={() => setLevels([])}
                >
                  None
                </button>
              </>
            ) : null}
            {GRADE_LEVELS.map((g) => (
              <button
                key={g}
                type="button"
                className={`al-cb-grade${levels.includes(g) ? " al-cb-grade--on" : ""}`}
                disabled={readOnly || saving}
                aria-pressed={levels.includes(g)}
                onClick={() =>
                  setLevels((prev) => (prev.includes(g) ? prev.filter((x) => x !== g) : [...prev, g]))
                }
              >
                {g}
              </button>
            ))}
          </div>
          </div>

          <p className="al-cb-hours">
            Total: {hours.all}
            {hours.byBucket.map((b) => (
              <span key={b.value + b.label}> · {b.label}: {b.value}</span>
            ))}
          </p>

          <div data-tour="cb-rubric">
          <span className="al-cb-label">Grade rubric</span>
          <button
            type="button"
            className={`al-cb-badge${rubricOpen ? " al-cb-badge--on" : ""}`}
            aria-haspopup="dialog"
            aria-expanded={rubricOpen}
            onClick={() => setRubricOpen(true)}
          >
            {chosenRubric ? chosenRubric.title : "No rubric"}
          </button>
          </div>
          <Dialog
            open={rubricOpen}
            title={chosenRubric ? chosenRubric.title : "No rubric"}
            subtitle="Grade rubric"
            onClose={() => setRubricOpen(false)}
            width="32rem"
          >
            <div className="al-cb-rubric-dialog">
              {chosenRubric ? (
                <div className="al-cb-rubric-groups">
                  {(["points", "minutes"] as const)
                    .map((measure) => ({
                      measure,
                      rows: chosenRubric.categories.filter((category) => category.measure === measure),
                    }))
                    .filter((group) => group.rows.length > 0)
                    .map((group) => (
                      <section key={group.measure} className="al-cb-rubric-group">
                        <h3>{group.measure === "points" ? "Points" : "Minutes"}</h3>
                        <ul>
                          {group.rows.map((category) => {
                            const included = category.activityTypes
                              .map((type) => ITEM_TYPES.find((row) => row.type === type)?.label || type)
                              .join(", ");
                            const cut =
                              category.cutWeight == null
                                ? "No cut"
                                : category.cutWeight !== category.weight
                                  ? `Cut ${category.cutWeight}`
                                  : "";
                            return (
                              <li key={`${category.label}-${category.measure}`}>
                                <div className="al-cb-rubric-row">
                                  <span>{category.label}</span>
                                  <span>{category.weight}%</span>
                                </div>
                                <p>{[included, cut].filter(Boolean).join(" · ")}</p>
                              </li>
                            );
                          })}
                        </ul>
                      </section>
                    ))}
                </div>
              ) : (
                <p className="al-cb-muted">No rubric. The course grade is unweighted points on marked work.</p>
              )}
              <div className="al-cb-rubric-switch">
                <button
                  type="button"
                  className={!rubricId ? "is-on" : ""}
                  disabled={!canSwitchRubric && Boolean(rubricId)}
                  onClick={() => {
                    if (!canSwitchRubric) return;
                    setRubricId("");
                  }}
                >
                  None
                </button>
                {rubricOptions.map((rubric) => (
                  <button
                    key={rubric.id}
                    type="button"
                    className={rubric.id === rubricId ? "is-on" : ""}
                    disabled={!canSwitchRubric && rubric.id !== rubricId}
                    onClick={() => {
                      if (!canSwitchRubric) return;
                      setRubricId(rubric.id);
                    }}
                  >
                    {rubric.title}
                    {rubric.isFoundation ? "" : " · copy"}
                  </button>
                ))}
              </div>
              {rubricLocked ? (
                <p className="al-cb-muted">This course already has student progress, so the rubric stays.</p>
              ) : (
                <p className="al-cb-muted">
                  {chosenRubric?.isFoundation
                    ? "Foundation. Editing it in Grade rubrics changes every course still using it."
                    : "Weights are edited on the rubric, not here."}
                  {canManageRubrics ? (
                    <>
                      {" "}
                      <Link to="/grade-rubrics">Grade rubrics</Link>
                    </>
                  ) : null}
                </p>
              )}
            </div>
          </Dialog>
        </div>
      ) : null}
      {!readOnly && saveShown
        ? createPortal(
            <button
              type="button"
              className={`al-cb-details-save${
                saveLeaving ? " is-leaving" : saveMotion === "in" ? " is-in" : saveMotion === "bump" ? " is-bump" : ""
              }`}
              disabled={saving || saveLeaving}
              onClick={() => void save()}
              onAnimationEnd={(event) => {
                if (event.animationName === "al-cb-details-save-in" || event.animationName === "al-cb-details-save-bounce") {
                  setSaveMotion("idle");
                }
              }}
            >
              {writeActionLabel(proposeOnly, saving, "Save details")}
            </button>,
            document.body,
          )
        : null}
      {syllabusPickerOpen ? (
        <Dialog
          open
          title="Syllabus"
          subtitle="PDF files you have uploaded"
          width="28rem"
          onClose={() => setSyllabusPickerOpen(false)}
        >
          {syllabusFiles == null ? (
            <p className="al-cb-muted">Loading…</p>
          ) : syllabusFiles.length === 0 ? (
            <p className="al-cb-muted">No PDFs yet. Upload one first.</p>
          ) : (
            <div className="al-cb-media-pick">
              {syllabusFiles.map((file) => (
                <button
                  key={file.id}
                  type="button"
                  className={`al-cb-media-pick-row${file.id === syllabus?.mediaId ? " al-cb-media-pick-row--on" : ""}`}
                  onClick={() => {
                    setSyllabus({
                      mediaId: file.id,
                      url: file.url,
                      name: file.originalFilename || "Syllabus.pdf",
                    });
                    setSyllabusPickerOpen(false);
                  }}
                >
                  <span className="al-cb-media-pick-thumb al-cb-media-pick-thumb--icon">
                    <Paperclip size={16} />
                  </span>
                  <span className="al-cb-media-pick-name">{file.originalFilename || file.url}</span>
                  <span className="al-cb-media-pick-meta">
                    {new Date(file.createdAt).toLocaleDateString()}
                  </span>
                </button>
              ))}
            </div>
          )}
        </Dialog>
      ) : null}
    </section>
  );
}


function ContinueRow({
  checked,
  disabled,
  onChange,
  title,
  subtext,
  extra,
}: {
  checked: boolean;
  disabled?: boolean;
  onChange?: (on: boolean) => void;
  title: ReactNode;
  subtext?: ReactNode;
  extra?: ReactNode;
}) {
  return (
    <label
      className={`al-cb-continue-row${checked ? " al-cb-continue-row--on" : ""}${disabled ? " al-cb-continue-row--locked" : ""}`}
    >
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled || !onChange}
        onChange={onChange ? (e) => onChange(e.target.checked) : undefined}
      />
      <span className="al-cb-continue-row-copy">
        <span className="al-cb-continue-row-title">{title}</span>
        {subtext ? <span className="al-cb-continue-row-sub">{subtext}</span> : null}
      </span>
      {extra ? <span className="al-cb-continue-row-extra">{extra}</span> : null}
    </label>
  );
}

type ItemNavId = "setup" | "content" | "special" | "questions";
type ItemNavJump = { id: ItemNavId; dir: "up" | "down" };

function itemNavLabel(id: ItemNavId, special: string | null | undefined) {
  if (id === "setup") return "Details";
  if (id === "content") return "Body";
  if (id === "questions") return "Questions";
  return special === "key_terms" ? "Key terms" : "Slides";
}

function itemNavDir(el: HTMLElement, scroller: HTMLElement): "up" | "down" {
  const view = scroller.getBoundingClientRect();
  const box = el.getBoundingClientRect();
  return box.bottom < view.top + 8 ? "up" : "down";
}

function scrollDialogSection(el: HTMLElement | null) {
  const scroller = el?.closest(".al-dialog-body");
  if (!el || !(scroller instanceof HTMLElement)) return;
  const top = el.getBoundingClientRect().top - scroller.getBoundingClientRect().top + scroller.scrollTop;
  scroller.scrollTo({ top: Math.max(0, top - 8), behavior: "smooth" });
}

function ItemSectionTitle({
  icon: Icon,
  children,
}: {
  icon: ComponentType<{ size?: number | string; className?: string }>;
  children: ReactNode;
}) {
  return (
    <h3 className="al-cb-item-section-title">
      <Icon size={14} aria-hidden="true" />
      {children}
    </h3>
  );
}

// ---------------------------------------------------------------------------
// Item editor dialog — common fields on every type, then per-type editors
// ---------------------------------------------------------------------------

function ItemEditorDialog({
  item,
  structure,
  coursePrimaryTaxonomy,
  readOnly,
  proposeOnly,
  tourPanel = null,
  onClose,
  onSaved,
  showError,
  toastSuccess,
}: {
  item: Item;
  structure: Structure;
  coursePrimaryTaxonomy: string | null;
  readOnly: boolean;
  proposeOnly: boolean;
  tourPanel?: WalkEditorPanel | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
  showError: (title: string, err: unknown) => void;
  toastSuccess: (msg: string) => void;
}) {
  const { capabilities } = useAuth();
  const { notice } = useNotification();
  const canOverrideDefaults = Boolean(capabilities?.overrideActivityDefaults);
  const typeSpec = ITEM_TYPES.find((t) => t.type === item.type);
  const anatomy = activitySpec(item.type);
  const sections = activitySections(item.type);
  const c = item.content as Record<string, unknown>;

  const [title, setTitle] = useState(item.title || "");
  const [bodyParts, setBodyParts] = useState<DraftPart[]>(() => defaultDraftParts(item.type, item.body));
  const settingDefault = (key: SettingKey) => anatomy.settings[key].default;
  const [estimatedMinutes, setEstimatedMinutes] = useState(Number(item.estimatedMinutes ?? 0));
  const [minuteTypeSplits, setMinuteTypeSplits] = useState<MinuteTypeSplits>(
    () => (item.minuteTypeSplits && typeof item.minuteTypeSplits === "object" ? item.minuteTypeSplits : {}),
  );
  const [secondaryTaxonomy, setSecondaryTaxonomy] = useState(item.secondaryTaxonomy || "");
  const [tertiaryTaxonomy, setTertiaryTaxonomy] = useState(item.tertiaryTaxonomy || "");
  const [categoryOptions, setCategoryOptions] = useState<TaxonomyOption[]>([]);
  const [categoryLoading, setCategoryLoading] = useState(true);
  const [required, setRequired] = useState(
    item.required == null ? settingDefault("required") !== false : item.required !== false,
  );

  const [terms, setTerms] = useState<KeyTerm[]>(Array.isArray(c.terms) ? (c.terms as KeyTerm[]) : []);
  const [decks, setDecks] = useState<SlideDeck[]>(() => {
    const rows = normalizeSlideDecks(c.decks);
    const mode = inferSlideMode(c.slideMode, rows);
    return mode && !rows.length ? [emptySlideDeck()] : rows;
  });
  const [slideMode, setSlideMode] = useState<SlideMode | null>(() =>
    inferSlideMode(c.slideMode, normalizeSlideDecks(c.decks)),
  );
  const [board, setBoard] = useState(() =>
    normalizeDiscussionBoard((item.metadata as { board?: unknown } | undefined)?.board ?? c.board),
  );
  const [assignmentPoints, setAssignmentPoints] = useState(() =>
    assignmentPointsOf(item.type, item.metadata as { assignmentPoints?: unknown; hasQuestions?: unknown }) ?? 10,
  );
  const [pointsAward, setPointsAward] = useState<PointsAward>(() =>
    pointsAwardOf(item.type, item.metadata as { assignmentPoints?: unknown; hasQuestions?: unknown; pointsAward?: unknown }) ??
    defaultPointsAward(item.type),
  );
  const [completionPoints, setCompletionPoints] = useState<number | null>(() => {
    const meta = item.metadata as {
      assignmentPoints?: unknown;
      hasQuestions?: unknown;
      pointsAward?: unknown;
      completionPoints?: unknown;
    };
    const possible = assignmentPointsOf(item.type, meta) ?? 0;
    const earned = completionAwardOf(item.type, meta);
    return earned != null && possible > earned ? earned : null;
  });
  const [includePoints, setIncludePoints] = useState(() => {
    // Discussion stays on and locked in the UI (grade weights). A legacy row
    // with no stored assignmentPoints is still ungraded until this is saved.
    if (anatomy.special === "discussion") return true;
    const pts = (item.metadata as { assignmentPoints?: unknown } | undefined)?.assignmentPoints;
    return pts != null && pts !== "";
  });

  const [dueAt, setDueAt] = useState(toLocalInput(item.dueAt));
  const [timeLimit, setTimeLimit] = useState(() => {
    const n = Number(item.timeLimitMinutes ?? 0);
    return n > 0 ? n : 30;
  });
  // Show what is STORED, never the type default. A null row means unlimited
  // attempts for the student, so painting "1" here from the exam preset told
  // the teacher one thing and let the student do another.
  const [maxAttempts, setMaxAttempts] = useState<number | "">(
    item.maxAttempts == null ? "" : Number(item.maxAttempts),
  );
  const [requirePrevious, setRequirePrevious] = useState(
    item.requirePrevious == null
      ? settingDefault("requirePrevious") !== false
      : item.requirePrevious !== false,
  );
  const [hasQuestions, setHasQuestions] = useState(() =>
    activityHasQuestions(item.type, item.metadata as { hasQuestions?: unknown }),
  );
  const [requireKnowledge, setRequireKnowledge] = useState(() =>
    activityRequireKnowledgeOn(item.metadata as { requireKnowledge?: unknown }),
  );
  const [includes, setIncludes] = useState<ActivityIncludes>(() =>
    activityIncludes(item.type, item.metadata as { includes?: unknown }, {
      dueAt: item.dueAt,
      timeLimitMinutes: item.timeLimitMinutes,
      maxAttempts: item.maxAttempts,
    }),
  );
  const [continueSettings, setContinueSettings] = useState<ContinueSettings>(() =>
    normalizeContinueSettings(item.continueSettings, item.type, {
      hasQuestions: activityHasQuestions(item.type, item.metadata as { hasQuestions?: unknown }),
      requireKnowledge: activityRequireKnowledgeOn(item.metadata as { requireKnowledge?: unknown }),
    }),
  );
  const [submissionSettings, setSubmissionSettings] = useState<SubmissionSettings>(() =>
    resolveSubmissionSettings(item),
  );
  const [studentMayMessage, setStudentMayMessage] = useState(() =>
    activityStudentMayMessage(item.type, item.metadata as { studentMayMessage?: unknown }, item),
  );

  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(true);
  const [discardOpen, setDiscardOpen] = useState(false);
  const [revertOpen, setRevertOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [continueOpen, setContinueOpen] = useState(false);
  const [submissionOpen, setSubmissionOpen] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [hiddenNav, setHiddenNav] = useState<ItemNavJump[]>([]);

  useEffect(() => {
    if (tourPanel === "continue") {
      setSubmissionOpen(false);
      setSettingsOpen(false);
      if (!readOnly) setContinueOpen(true);
      return;
    }
    if (tourPanel === "submission") {
      setContinueOpen(false);
      setSettingsOpen(false);
      if (hasQuestions) setSubmissionOpen(true);
      return;
    }
    if (tourPanel === "settings") {
      setContinueOpen(false);
      setSubmissionOpen(false);
      if (canOverrideDefaults) setSettingsOpen(true);
      return;
    }
    setContinueOpen(false);
    setSubmissionOpen(false);
    setSettingsOpen(false);
  }, [tourPanel, readOnly, hasQuestions, canOverrideDefaults]);
  const [hint, setHint] = useState<null | "minutes" | "points">(null);
  const closeTimer = useRef<number | null>(null);
  const setupRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const specialRef = useRef<HTMLDivElement | null>(null);
  const questionsRef = useRef<HTMLDivElement | null>(null);
  const questionsHeadRef = useRef<HTMLDivElement | null>(null);
  const [dockAddQuestion, setDockAddQuestion] = useState(false);

  const snapshot = JSON.stringify({
    title, bodyParts, dueAt, estimatedMinutes, minuteTypeSplits, secondaryTaxonomy, tertiaryTaxonomy, required,
    terms, decks, slideMode, board, assignmentPoints, pointsAward, completionPoints, includePoints, maxAttempts, timeLimit, requirePrevious,
    hasQuestions, requireKnowledge, includes, continueSettings, submissionSettings, studentMayMessage,
  });
  const [initialSnapshot] = useState(snapshot);
  const dirty = snapshot !== initialSnapshot;

  const finishClose = () => {
    if (closeTimer.current !== null) return;
    setOpen(false);
    closeTimer.current = window.setTimeout(onClose, DIALOG_EXIT_MS);
  };

  const requestClose = () => {
    if (dirty) {
      setDiscardOpen(true);
      return;
    }
    finishClose();
  };

  useEffect(
    () => () => {
      if (closeTimer.current !== null) window.clearTimeout(closeTimer.current);
    },
    [],
  );

  const [questions, setQuestions] = useState<Question[]>([]);
  const [questionsLoading, setQuestionsLoading] = useState(hasQuestions);
  const [questionModal, setQuestionModal] = useState<Question | "new" | null>(null);

  const loadQuestions = useCallback(async () => {
    setQuestionsLoading(true);
    try {
      const envelope = await api<{ success: boolean; data: Question[] }>(
        `/api/activities/${item.id}/questions`,
      );
      setQuestions(Array.isArray(envelope.data) ? envelope.data : []);
    } catch (err) {
      showError("Could not load questions", err);
    } finally {
      setQuestionsLoading(false);
    }
  }, [item.id, showError]);

  useEffect(() => {
    if (hasQuestions) void loadQuestions();
  }, [hasQuestions, loadQuestions]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const envelope = await api<{ success: boolean; data: TaxonomyOption[] }>("/api/courses/taxonomy");
        if (!cancelled) setCategoryOptions(Array.isArray(envelope.data) ? envelope.data : []);
      } catch {
        /* pickers still render */
      } finally {
        if (!cancelled) setCategoryLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const saveQuestionField = useCallback(
    async (questionId: string, body: { points?: number; attemptPoints?: number }) => {
      try {
        const result = await patch<{
          success?: boolean;
          data?: Question & { revertedToDraft?: boolean; activityRevertedToDraft?: boolean };
        }>(`/api/activities/questions/${questionId}`, body);
        const saved = result?.data;
        if (saved?.revertedToDraft || saved?.activityRevertedToDraft) {
          const gaps = questionIncompleteReasons(saved);
          notice({
            type: "warning",
            title: "Reverted to Draft",
            subtext: saved.activityRevertedToDraft
              ? "This item is incomplete, so it is no longer published."
              : gaps.join(" ") ||
                "The question is incomplete, so it can no longer stay Live.",
            duration: 7000,
          });
        }
        await loadQuestions();
        await onSaved();
      } catch (err) {
        showError("Could not save points", err);
      }
    },
    [loadQuestions, notice, onSaved, showError],
  );

  const toggleQuestionPublish = useCallback(
    async (question: Question, next: boolean) => {
      if (next && !questionIsComplete(question)) {
        const gaps = questionIncompleteReasons(question);
        notice({
          type: "warning",
          title: "Finish the question first",
          subtext: gaps.join(" ") || "A question has to be complete before it can go Live.",
          duration: 7000,
        });
        return;
      }
      try {
        await patch(`/api/activities/questions/${question.id}`, { published: next });
        await loadQuestions();
      } catch (err) {
        showError("Could not change publish state", err);
      }
    },
    [loadQuestions, notice, showError],
  );

  useEffect(() => {
    if (!hasQuestions) {
      setDockAddQuestion(false);
      return undefined;
    }
    const el = questionsHeadRef.current;
    if (!el) return undefined;
    const root = el.closest(".al-dialog-body");
    const io = new IntersectionObserver(
      ([entry]) => setDockAddQuestion(!entry.isIntersecting),
      { root: root instanceof Element ? root : null, threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, [hasQuestions]);

  const showContentNav = Boolean(sections[0]);
  const showSpecialNav = anatomy.special === "slides" || anatomy.special === "key_terms";
  useEffect(() => {
    const scroller = setupRef.current?.closest(".al-dialog-body");
    if (!(scroller instanceof HTMLElement)) return undefined;
    const watched: Array<[ItemNavId, HTMLElement]> = (
      [
        ["setup", setupRef.current],
        ["content", contentRef.current],
        ["special", specialRef.current],
        ["questions", questionsRef.current],
      ] as Array<[ItemNavId, HTMLElement | null]>
    ).filter((row): row is [ItemNavId, HTMLElement] => Boolean(row[1]));
    if (watched.length < 2) {
      setHiddenNav([]);
      return undefined;
    }

    const visible = new Map<ItemNavId, boolean>(watched.map(([id]) => [id, true]));
    const publish = () => {
      setHiddenNav(
        watched
          .filter(([id]) => visible.get(id) === false)
          .map(([id, el]) => ({ id, dir: itemNavDir(el, scroller) })),
      );
    };
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const id = (entry.target as HTMLElement).dataset.itemNav as ItemNavId | undefined;
          if (id) visible.set(id, entry.isIntersecting);
        }
        publish();
      },
      { root: scroller, threshold: 0, rootMargin: "-12px 0px" },
    );
    for (const [, el] of watched) io.observe(el);
    return () => {
      io.disconnect();
      setHiddenNav([]);
    };
  }, [hasQuestions, showContentNav, showSpecialNav]);

  const jumpTo = (id: ItemNavId) => {
    scrollDialogSection(
      id === "setup"
        ? setupRef.current
        : id === "content"
          ? contentRef.current
          : id === "special"
            ? specialRef.current
            : questionsRef.current,
    );
  };

  const save = async (opts?: { allowRevert?: boolean }) => {
    const content: Record<string, unknown> = { ...c };
    delete content.html;
    delete content.required;
    delete content.requirePrevious;
    delete content.estimatedMinutes;
    delete content.hourBucket;
    delete content.totalPoints;
    delete content.timeLimitMinutes;
    delete content.maxAttempts;
    delete content.mode;
    delete content.outsideMinutes;
    delete content.submissionTypes;
    delete content.materials;
    delete content.materialsHtml;
    delete content.board;
    if (anatomy.special === "key_terms") {
      content.terms = terms.filter((t) => t.term.trim() || t.definition.trim());
    }
    if (anatomy.special === "slides") {
      content.decks = decks.filter(
        (d) => deckHasSource(d) || Boolean(d.title.trim()) || d.maps.length > 0
      );
      if (slideMode) content.slideMode = slideMode;
      else delete content.slideMode;
    }
    const metadata: Record<string, unknown> = { ...(item.metadata || {}) };
    metadata.hasQuestions = hasQuestions;
    if (requireKnowledge) delete metadata.requireKnowledge;
    else metadata.requireKnowledge = false;
    metadata.includes = includes;
    metadata.studentMayMessage = studentMayMessage;
    delete metadata.submissionSettings;
    if (anatomy.special === "discussion") metadata.board = board;
    else delete metadata.board;
    if (!hasQuestions && (anatomy.special === "discussion" || includePoints)) {
      metadata.assignmentPoints = assignmentPoints;
      metadata.pointsAward = pointsAward;
      if (
        pointsAward === "on_complete" &&
        completionPoints != null &&
        completionPoints >= 1 &&
        completionPoints < assignmentPoints
      ) {
        metadata.completionPoints = completionPoints;
      } else {
        delete metadata.completionPoints;
      }
    } else {
      delete metadata.assignmentPoints;
      delete metadata.pointsAward;
      delete metadata.completionPoints;
    }
    const nextBody = bodyParts.map(fromDraftPart).filter(partHasContent);
    const gaps = incompleteReasons({
      type: item.type,
      body: nextBody,
      content,
      metadata,
      questionCount: hasQuestions ? (questionsLoading ? item.questionCount : questions.length) : 0,
      questions: hasQuestions && !questionsLoading ? questions : undefined,
    });
    if (item.published && gaps.length && !opts?.allowRevert) {
      setRevertOpen(true);
      return;
    }
    const typesErr = activityMinuteTypesInvalid({
      options: categoryOptions,
      coursePrimaryId: coursePrimaryTaxonomy || "",
      secondaryId: secondaryTaxonomy,
      tertiaryId: tertiaryTaxonomy,
      estimatedMinutes,
      minuteTypeSplits,
    });
    if (typesErr) {
      showError("Minute types", new Error(typesErr));
      return;
    }
    setSaving(true);
    try {
      const result = await patch<{
        success?: boolean;
        proposed?: boolean;
        data?: Item & { revertedToDraft?: boolean };
      }>(`/api/activities/${item.id}`, {
        title,
        body: nextBody,
        required,
        requirePrevious,
        estimatedMinutes,
        secondaryTaxonomy: secondaryTaxonomy || null,
        tertiaryTaxonomy: tertiaryTaxonomy || null,
        minuteTypeSplits,
        timeLimitMinutes: includes.timeLimitMinutes ? Math.max(1, timeLimit) : 0,
        maxAttempts: includes.maxAttempts ? (maxAttempts === "" ? null : maxAttempts) : null,
        content,
        metadata,
        continueSettings,
        submissionSettings,
        dueAt: includes.dueAt && dueAt ? new Date(dueAt).toISOString() : null,
      });
      const reverted = Boolean(result?.data?.revertedToDraft) && !isProposed(result);
      if (gaps.length) {
        notice({
          type: "warning",
          title: isProposed(result)
            ? "Proposed — still incomplete"
            : reverted
              ? "Reverted to Draft"
              : "Saved — still incomplete",
          subtext: reverted
            ? `${gaps.join(" ")} It cannot stay published while incomplete.`
            : gaps.join(" "),
          duration: 7000,
        });
      } else {
        toastSuccess(isProposed(result) ? "Proposed" : "Saved");
      }
      await onSaved();
      finishClose();
    } catch (err) {
      showError("Could not save the item", err);
    } finally {
      setSaving(false);
    }
  };

  const listedQuestionCount = questionsLoading ? item.questionCount : questions.length;
  const listedQuestionPts = questionsLoading
    ? item.totalPoints
    : questions.reduce((sum, q) => sum + (Number(q.points) || 0), 0);
  const shownSettings = visibleSettings(item.type, includes);
  const passAttempts = includes.maxAttempts && maxAttempts !== "" ? Number(maxAttempts) : null;
  const passAttemptsWarn =
    passAttempts === 1
      ? `This item allows 1 attempt. A student below ${continueSettings.minPercent}% cannot continue. Raise max attempts or leave them unlimited.`
      : passAttempts != null
        ? `Check max attempts (${passAttempts}). After the last try, a score below ${continueSettings.minPercent}% locks the path.`
        : `Max attempts are unlimited right now. If you cap them, leave enough retries or a fail locks the student on this step.`;

  const setInclude = (key: IncludeSettingKey, on: boolean) => {
    setIncludes((prev) => ({ ...prev, [key]: on }));
    if (!on) return;
    if (key === "timeLimitMinutes" && !timeLimit) setTimeLimit(30);
  };

  const renderSetting = (key: SettingKey, disabled: boolean) => {
    const meta = SETTING_META[key];
    if (key === "dueAt") {
      return (
        <div key={key}>
          <label className="al-cb-label">{meta.label}</label>
          <input
            type="datetime-local"
            className="al-cb-input"
            value={dueAt}
            disabled={disabled}
            onChange={(e) => setDueAt(e.target.value)}
          />
        </div>
      );
    }
    if (key === "timeLimitMinutes") {
      return (
        <div key={key}>
          <label className="al-cb-label">{meta.label}</label>
          <input
            type="number"
            min={1}
            className="al-cb-input"
            value={timeLimit}
            disabled={disabled}
            onChange={(e) => setTimeLimit(Math.max(1, Number(e.target.value) || 1))}
          />
        </div>
      );
    }
    if (key === "maxAttempts") {
      return (
        <div key={key}>
          <label className="al-cb-label">{meta.label}</label>
          <input
            type="number"
            min={1}
            className="al-cb-input"
            title={meta.hint}
            placeholder="Unlimited"
            value={maxAttempts}
            disabled={disabled}
            onChange={(e) => {
              const raw = e.target.value;
              setMaxAttempts(raw === "" ? "" : Math.max(1, Number(raw) || 1));
            }}
          />
        </div>
      );
    }
    const checked = key === "required" ? required : requirePrevious;
    const onToggle = key === "required" ? setRequired : setRequirePrevious;
    return (
      <label key={key} className="al-cb-check" title={meta.hint}>
        <input
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(e) => onToggle(e.target.checked)}
        />
        {meta.label}
      </label>
    );
  };

  const renderAssignmentPoints = (disabled: boolean) => (
    <div data-tour="cb-item-points">
      <label className="al-cb-label">Points</label>
      <input
        type="number"
        min={0}
        className="al-cb-input"
        value={assignmentPoints}
        disabled={disabled}
        onChange={(e) => {
          const next = Math.max(0, Math.trunc(Number(e.target.value) || 0));
          setAssignmentPoints(next);
          setCompletionPoints((prev) => (prev != null && prev >= next ? null : prev));
        }}
      />
      <p className="al-cb-sub">0 means this activity is not graded.</p>
      <div className="al-cb-award">
        <p className="al-cb-label">How these points are awarded</p>
        <div className="al-cb-continue-list">
          <div
            className={`al-cb-continue-row${pointsAward === "on_complete" ? " al-cb-continue-row--on" : ""}${disabled ? " al-cb-continue-row--locked" : ""}`}
          >
            <input
              id={`points-award-complete-${item.id}`}
              type="radio"
              name={`points-award-${item.id}`}
              checked={pointsAward === "on_complete"}
              disabled={disabled}
              onChange={() => setPointsAward("on_complete")}
            />
            <span className="al-cb-continue-row-copy">
              <label className="al-cb-continue-row-title" htmlFor={`points-award-complete-${item.id}`}>
                Award points on completion
              </label>
              <span className="al-cb-continue-row-sub">
                {anatomy.special === "discussion"
                  ? "They receive this once the reply rules are met and they click Complete."
                  : "They receive this once they click Complete. Continue rules, such as time on the step or watching the video, still have to be met first."}
                {" "}
                All gives the whole total, and follows that total if you change it. A number gives exactly that many. The rest is not awarded.
              </span>
              {pointsAward === "on_complete" ? (
                <span className="al-cb-award-amount">
                  <label>
                    <input
                      type="radio"
                      name={`completion-amount-${item.id}`}
                      checked={completionPoints == null}
                      disabled={disabled}
                      onChange={() => setCompletionPoints(null)}
                    />
                    All
                  </label>
                  <label>
                    <input
                      type="radio"
                      name={`completion-amount-${item.id}`}
                      checked={completionPoints != null}
                      disabled={disabled || assignmentPoints < 2}
                      onChange={() =>
                        setCompletionPoints((prev) =>
                          prev != null && prev < assignmentPoints ? prev : 1,
                        )
                      }
                    />
                    <input
                      type="number"
                      min={1}
                      max={Math.max(1, assignmentPoints - 1)}
                      className="al-cb-input al-cb-input--inline"
                      aria-label="Points awarded on completion"
                      value={completionPoints ?? ""}
                      placeholder="Points"
                      disabled={disabled || assignmentPoints < 2}
                      onChange={(e) => {
                        const raw = e.target.value;
                        if (raw === "") {
                          setCompletionPoints(null);
                          return;
                        }
                        const n = Math.trunc(Number(raw));
                        if (!Number.isInteger(n) || n < 1) return;
                        setCompletionPoints(n >= assignmentPoints ? null : n);
                      }}
                    />
                  </label>
                </span>
              ) : null}
            </span>
          </div>
          <label
            className={`al-cb-continue-row${pointsAward === "manual" ? " al-cb-continue-row--on" : ""}${disabled ? " al-cb-continue-row--locked" : ""}`}
          >
            <input
              type="radio"
              name={`points-award-${item.id}`}
              checked={pointsAward === "manual"}
              disabled={disabled}
              onChange={() => setPointsAward("manual")}
            />
            <span className="al-cb-continue-row-copy">
              <span className="al-cb-continue-row-title">Mark by hand</span>
              <span className="al-cb-continue-row-sub">
                {anatomy.special === "discussion"
                  ? "You mark each student's prompt post out of this number."
                  : "You mark the finished work out of this number."}
              </span>
            </span>
          </label>
        </div>
      </div>
    </div>
  );

  return (
    <>
    <Dialog
      open={open}
      title={typeSpec ? typeSpec.label : item.type}
      subtitle={
        isProposedNode(item)
          ? "Proposed — only you and reviewers see this. Students do not."
          : typeSpec?.description
      }
      width="40rem"
      maxHeight="min(92dvh, calc(100dvh - 2rem))"
      escapeClose={!discardOpen && !revertOpen && !settingsOpen && !continueOpen && !submissionOpen && !previewOpen && !hint && !questionModal}
      onClose={requestClose}
      headerActions={
        <>
          <button
            type="button"
            className="al-cb-icon-btn"
            aria-label="Preview as student"
            title="Preview as student"
            onClick={() => setPreviewOpen(true)}
          >
            <Eye size={16} aria-hidden="true" />
          </button>
          {!readOnly ? (
            <>
              <button
                type="button"
                className="al-cb-icon-btn"
                data-tour="cb-item-continue"
                aria-label="Continue settings"
                title="Continue settings"
                disabled={saving}
                onClick={() => setContinueOpen(true)}
              >
                <ListChecks size={16} aria-hidden="true" />
              </button>
              {hasQuestions ? (
                <button
                  type="button"
                  className="al-cb-icon-btn"
                  data-tour="cb-item-submission"
                  aria-label="Submission settings"
                  title="Submission settings"
                  disabled={saving}
                  onClick={() => setSubmissionOpen(true)}
                >
                  <ClipboardList size={16} aria-hidden="true" />
                </button>
              ) : null}
              {canOverrideDefaults ? (
                <button
                  type="button"
                  className="al-cb-icon-btn"
                  data-tour="cb-item-settings"
                  aria-label="Activity settings"
                  title="Activity settings"
                  disabled={saving}
                  onClick={() => setSettingsOpen(true)}
                >
                  <Settings2 size={16} aria-hidden="true" />
                </button>
              ) : null}
            </>
          ) : null}
        </>
      }
      slideFooter
      footer={
        !readOnly && (dirty || saving) ? (
          <button
            type="button"
            className="al-cb-btn al-cb-btn--primary al-cb-btn--pulse"
            disabled={saving}
            onClick={() => void save()}
          >
            {saving
              ? proposeOnly && !isProposedNode(item)
                ? "PROPOSING…"
                : "SAVING…"
              : proposeOnly && !isProposedNode(item)
                ? "PROPOSE"
                : "SAVE"}
          </button>
        ) : null
      }
      sideRail={
        hiddenNav.length ? (
          <nav className="al-cb-item-rail" aria-label="Jump to section">
            {hiddenNav.map(({ id, dir }) => {
              const label = itemNavLabel(id, anatomy.special);
              return (
                <button
                  key={id}
                  type="button"
                  className="al-cb-item-rail-btn"
                  aria-label={`Jump ${dir === "up" ? "up" : "down"} to ${label}`}
                  onClick={() => jumpTo(id)}
                >
                  {dir === "up" ? (
                    <ArrowUp size={15} aria-hidden="true" />
                  ) : (
                    <ArrowDown size={15} aria-hidden="true" />
                  )}
                  {label}
                </button>
              );
            })}
          </nav>
        ) : null
      }
    >
      <div ref={setupRef} id="al-cb-item-setup" data-item-nav="setup">
      {isProposedNode(item) ? (
        <p className="al-cb-proposed-note">
          This item is proposed. You can keep editing it. It is not on the student outline until a reviewer applies it.
        </p>
      ) : null}
      <ItemSectionTitle icon={LayoutTemplate}>Details</ItemSectionTitle>
      <label className="al-cb-label">Title</label>
      <input
        className="al-cb-input"
        data-tour="cb-item-title"
        placeholder={`Untitled ${typeSpec ? typeSpec.label : item.type}`}
        value={title}
        disabled={readOnly || saving}
        onChange={(e) => setTitle(e.target.value)}
      />

      <label className="al-cb-label al-cb-label--with-hint">
        Estimated minutes
        <HintButton label="About estimated minutes" onClick={() => setHint("minutes")} />
      </label>
      <input
        type="number"
        min={0}
        className="al-cb-input"
        data-tour="cb-item-minutes"
        value={estimatedMinutes}
        disabled={readOnly || saving}
        onChange={(e) => setEstimatedMinutes(Number(e.target.value))}
      />

      <ActivityCategoryFields
        coursePrimaryId={coursePrimaryTaxonomy || ""}
        secondaryId={secondaryTaxonomy}
        tertiaryId={tertiaryTaxonomy}
        estimatedMinutes={estimatedMinutes}
        minuteTypeSplits={minuteTypeSplits}
        options={categoryOptions}
        loading={categoryLoading}
        disabled={readOnly || saving}
        onSecondary={setSecondaryTaxonomy}
        onTertiary={setTertiaryTaxonomy}
        onMinuteTypeSplits={setMinuteTypeSplits}
        onOptionsChange={setCategoryOptions}
      />

      {shownSettings.length ? (
        <div className={`al-cb-field-row${shownSettings.length >= 4 ? " al-cb-field-row--four" : ""}`}>
          {shownSettings.map((key) => renderSetting(key, readOnly || saving))}
        </div>
      ) : null}
      {hasQuestions ? (
        <div className="al-cb-award">
          <p className="al-cb-label">How these points are awarded</p>
          <p className="al-cb-sub">
            This activity has questions, so the points come from the questions.
            You cannot set awarding here. Each question has its own grading.
          </p>
        </div>
      ) : anatomy.special === "discussion" || includePoints ? (
        renderAssignmentPoints(readOnly || saving)
      ) : null}

      </div>

      {sections[0] ? (
        <div ref={contentRef} id="al-cb-item-content" className="al-cb-item-block" data-item-nav="content" data-tour="cb-item-body">
        <BodyPartsEditor
          label="Body"
          placeholder={sections[0].placeholder || undefined}
          rows={sections[0].rows ?? 4}
          parts={bodyParts}
          disabled={readOnly || saving}
          onChange={setBodyParts}
          wordTarget={
            item.type === "reading" && item.targets?.minWords
              ? { min: item.targets.minWords, max: item.targets.maxWords }
              : null
          }
        />
        {requiredMediaPart(item.type) && !bodyHasRequiredMedia(item.type, bodyParts.map(fromDraftPart)) ? (
          <p className="al-cb-muted">
            {requiredMediaPart(item.type) === "audio"
              ? "Still incomplete until it has audio."
              : "Still incomplete until it has a video."}
          </p>
        ) : null}
        </div>
      ) : null}

      {anatomy.special === "slides" ? (
        <div ref={specialRef} id="al-cb-item-special" className="al-cb-item-block" data-item-nav="special" data-tour="cb-item-slides">
          <SlideDeckEditor
            decks={decks}
            outline={outlineTree(structure)}
            mode={slideMode}
            disabled={readOnly || saving}
            onChange={setDecks}
            onModeChange={setSlideMode}
          />
        </div>
      ) : null}

      {anatomy.special === "key_terms" ? (
        <div ref={specialRef} id="al-cb-item-special" className="al-cb-item-block" data-item-nav="special" data-tour="cb-item-terms">
          <div className="al-cb-terms">
            <div className="al-cb-questions-head">
              <ItemSectionTitle icon={BookMarked}>
                Key terms
                <span className="al-cb-item-section-meta">· {terms.length} — 7–10 recommended</span>
              </ItemSectionTitle>
              {!readOnly ? (
                <button
                  type="button"
                  className="al-cb-btn al-cb-btn--ghost"
                  onClick={() => setTerms((prev) => [...prev, { id: newLocalId(), term: "", definition: "" }])}
                >
                  <Plus size={13} aria-hidden="true" />
                  Add term
                </button>
              ) : null}
            </div>
            {terms.length === 0 ? <p className="al-cb-muted">No terms yet.</p> : null}
            {terms.map((t, index) => (
              <div key={t.id} className="al-cb-term-card">
                <span className="al-cb-question-n">{index + 1}</span>
                <div className="al-cb-term-fields">
                  <input
                    className="al-cb-input"
                    placeholder="Term"
                    value={t.term}
                    disabled={readOnly || saving}
                    onChange={(e) =>
                      setTerms((prev) => prev.map((x, i) => (i === index ? { ...x, term: e.target.value } : x)))
                    }
                  />
                  <textarea
                    className="al-cb-textarea"
                    rows={2}
                    placeholder="Student-friendly definition"
                    value={t.definition}
                    disabled={readOnly || saving}
                    onChange={(e) =>
                      setTerms((prev) =>
                        prev.map((x, i) => (i === index ? { ...x, definition: e.target.value } : x)),
                      )
                    }
                  />
                </div>
                {!readOnly ? (
                  <button
                    type="button"
                    className="al-cb-icon-btn al-cb-icon-btn--danger"
                    aria-label="Remove term"
                    onClick={() => setTerms((prev) => prev.filter((_, i) => i !== index))}
                  >
                    <Trash2 size={13} />
                  </button>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {hasQuestions ? (
        <div ref={questionsRef} id="al-cb-item-questions" className="al-cb-questions" data-item-nav="questions" data-tour="cb-item-questions">
          <div className="al-cb-questions-head" ref={questionsHeadRef}>
            <ItemSectionTitle icon={ClipboardList}>
              Questions
              <span className="al-cb-item-section-meta">
                ({listedQuestionCount}
                {listedQuestionPts ? ` · ${listedQuestionPts} pts` : ""}
                {!questionsLoading && questions.some((q) => !questionIsComplete(q))
                  ? ` · ${questions.filter((q) => !questionIsComplete(q)).length} incomplete`
                  : ""}
                {item.targets?.minItems ? ` · target ${item.targets.minItems}–${item.targets.maxItems}` : ""})
              </span>
              <HintButton label="About question points" onClick={() => setHint("points")} />
            </ItemSectionTitle>
            {!readOnly ? (
              <button type="button" className="al-cb-btn al-cb-btn--ghost" onClick={() => setQuestionModal("new")}>
                <Plus size={13} aria-hidden="true" />
                Add question
              </button>
            ) : null}
          </div>
          {questionsLoading ? (
            <p className="al-cb-muted al-cb-questions-loading">
              <Loader2 size={14} className="al-cb-spin" aria-hidden="true" />
              Loading questions…
            </p>
          ) : questions.length === 0 ? (
            <p className="al-cb-muted">No questions yet.</p>
          ) : (
            <div className="al-cb-qtable" role="table" aria-label="Questions">
              <div className="al-cb-qtable-head" role="row">
                <span role="columnheader">#</span>
                <span role="columnheader">Prompt/Label</span>
                <span role="columnheader">Question type</span>
                <span role="columnheader">Attempt Points</span>
                <span role="columnheader">Max points</span>
                <span role="columnheader">Live</span>
                <span role="columnheader">Grading</span>
                <span role="columnheader">Edit</span>
                <span role="columnheader">Delete</span>
              </div>
              {questions.map((question, index) => {
                const complete = questionIsComplete(question);
                const score = scoringBadge(question.type, question.scoring);
                const gaps = complete ? [] : questionIncompleteReasons(question);
                const published = question.published === true || question.status === "published";
                const typeLabel = questionTypeCaption(question.type, question.presentation);
                const isDirections = question.type === "directions";
                return (
                  <div
                    key={question.id}
                    role="row"
                    className={`al-cb-qcard${question.status === "proposed" ? " al-cb-proposed" : ""}`}
                  >
                    <span className="al-cb-qcard-n">{index + 1}</span>
                    <p
                      className="al-cb-qcard-prompt"
                      title={[
                        promptPreview(question.prompt),
                        question.status === "proposed" ? "Proposed" : "",
                        gaps[0] || "",
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    >
                      {promptPreview(question.prompt) || "Untitled question"}
                    </p>
                    <span className="al-cb-qcard-kind" title={typeLabel}>
                      {typeLabel}
                    </span>
                    <span className="al-cb-qcard-cell">
                      {!isDirections && question.awardAttemptPoints ? (
                        <PointsChip
                          value={question.attemptPoints ?? 0}
                          suffix="att"
                          title="Attempt points"
                          showSuffix={false}
                          disabled={readOnly}
                          max={Math.max(0, Number(question.points) - 1)}
                          onChange={(next) => void saveQuestionField(question.id, { attemptPoints: next })}
                        />
                      ) : (
                        <span className="al-cb-qcard-empty">—</span>
                      )}
                    </span>
                    <span className="al-cb-qcard-cell">
                      {!isDirections ? (
                        <PointsChip
                          value={question.points}
                          suffix="pts"
                          title="Max points"
                          showSuffix={false}
                          disabled={readOnly}
                          onChange={(next) => {
                            const body: { points: number; attemptPoints?: number } = { points: next };
                            if (question.awardAttemptPoints && (question.attemptPoints ?? 0) >= next) {
                              if (next <= 0) {
                                showError(
                                  "Could not save points",
                                  new Error("Attempt points must be less than the question points"),
                                );
                                return;
                              }
                              body.attemptPoints = next - 1;
                            }
                            void saveQuestionField(question.id, body);
                          }}
                        />
                      ) : (
                        <span className="al-cb-qcard-empty">—</span>
                      )}
                    </span>
                    <span className="al-cb-qcard-cell">
                      {question.status === "proposed" ? (
                        <span className="al-cb-qcard-empty">—</span>
                      ) : (
                        <PublishToggle
                          published={published}
                          disabled={readOnly || (!published && !complete)}
                          label={promptPreview(question.prompt) || "Question"}
                          title={
                            !published && !complete
                              ? "Finish the question before going Live"
                              : undefined
                          }
                          onToggle={() => void toggleQuestionPublish(question, !published)}
                        />
                      )}
                    </span>
                    <span className="al-cb-qcard-cell">
                      {score ? (
                        <span
                          className={
                            score.kind === "done" ? "al-cb-badge al-cb-badge--done" : "al-cb-badge al-cb-badge--todo"
                          }
                        >
                          {score.label}
                        </span>
                      ) : (
                        <span className="al-cb-qcard-empty">—</span>
                      )}
                    </span>
                    <span className="al-cb-qcard-cell">
                      <button
                        type="button"
                        className="al-cb-icon-btn"
                        aria-label="Edit question"
                        title="Edit"
                        onClick={() => setQuestionModal(question)}
                      >
                        <Pencil size={13} />
                      </button>
                    </span>
                    <span className="al-cb-qcard-cell">
                      {!readOnly ? (
                        <button
                          type="button"
                          className="al-cb-icon-btn al-cb-icon-btn--danger"
                          aria-label="Remove question"
                          title="Remove"
                          onClick={() =>
                            void (async () => {
                              try {
                                const result = await del<{
                                  success?: boolean;
                                  proposed?: boolean;
                                  data?: { revertedToDraft?: boolean };
                                }>(`/api/activities/${item.id}/questions/${question.id}`);
                                setQuestions((prev) => prev.filter((row) => row.id !== question.id));
                                if (result?.data?.revertedToDraft && !isProposed(result)) {
                                  notice({
                                    type: "warning",
                                    title: "Reverted to Draft",
                                    subtext: "This item is incomplete without its questions, so it is no longer published.",
                                    duration: 7000,
                                  });
                                } else {
                                  toastSuccess(isProposed(result) ? "Proposed" : "Question removed");
                                }
                                await loadQuestions();
                                await onSaved();
                              } catch (err) {
                                if (
                                  err instanceof ApiError &&
                                  (err.status === 404 || err.code === "QUESTION_NOT_FOUND")
                                ) {
                                  setQuestions((prev) => prev.filter((row) => row.id !== question.id));
                                  toastSuccess("Question removed");
                                  await loadQuestions();
                                  await onSaved();
                                  return;
                                }
                                showError("Could not remove the question", err);
                              }
                            })()
                          }
                        >
                          <Trash2 size={13} />
                        </button>
                      ) : (
                        <span className="al-cb-qcard-empty">—</span>
                      )}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          {!readOnly && dockAddQuestion ? (
            <div className="al-cb-q-add al-cb-q-add--dock">
              <button type="button" className="al-cb-btn al-cb-btn--ghost" onClick={() => setQuestionModal("new")}>
                <Plus size={13} aria-hidden="true" />
                Add question
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

    </Dialog>

    <StudentPreviewDialog open={previewOpen} onClose={() => setPreviewOpen(false)} frame="page">
      <StudentActivityPreview
        type={item.type}
        title={title}
        estimatedMinutes={estimatedMinutes}
        dueAt={dueAt}
        timeLimitMinutes={timeLimit}
        maxAttempts={maxAttempts}
        includes={includes}
        parts={bodyParts.map(fromDraftPart)}
        questions={questions}
        questionsLoading={questionsLoading}
        hasQuestions={hasQuestions}
        requireKnowledge={requireKnowledge}
        terms={terms}
        decks={decks}
        slideMode={slideMode}
        outlineTargets={flattenOutline(structure)}
      />
    </StudentPreviewDialog>

    {questionModal ? (
      <QuestionEditor
        activityId={item.id}
        question={questionModal === "new" ? null : questionModal}
        coursePrimaryTaxonomy={coursePrimaryTaxonomy}
        readOnly={readOnly}
        itemLabel={title || (typeSpec ? typeSpec.label : item.type)}
        onBack={() => setQuestionModal(null)}
        onSaved={async () => {
          await loadQuestions();
          await onSaved();
        }}
        proposeOnly={proposeOnly}
        showError={showError}
      />
    ) : null}

    {continueOpen ? (
      <Dialog
        open
        title="Continue settings"
        subtitle="What they must do to leave. Minutes here are hour credit for doing the step, not a grade. Points only come from questions."
        width="32rem"
        onClose={() => setContinueOpen(false)}
      >
        <p className="al-cb-style-hint">
          Every activity ends with Complete. If Require Knowledge is on and this
          item has questions, those must be submitted first. The switches below are extras.
        </p>
        <div className="al-cb-continue-list" data-tour="cb-continue-panel">
          <ContinueRow
            checked
            disabled
            title="Click Complete"
            subtext="Always last. The learner cannot leave without this click."
          />
          <ContinueRow
            checked={continueSettings.minDwellMinutes > 0}
            disabled={readOnly || saving}
            onChange={(on) =>
              setContinueSettings((prev) => ({
                ...prev,
                minDwellMinutes: on ? Math.max(1, prev.minDwellMinutes || 5) : 0,
              }))
            }
            title="Minimum time"
            subtext="They must stay on this step before Complete is available."
            extra={
              <>
                <input
                  type="number"
                  min={1}
                  className="al-cb-input al-cb-input--inline"
                  value={continueSettings.minDwellMinutes || 5}
                  disabled={readOnly || saving || continueSettings.minDwellMinutes <= 0}
                  aria-label="Minutes before Complete"
                  onChange={(e) =>
                    setContinueSettings((prev) => ({
                      ...prev,
                      minDwellMinutes: Math.max(1, Number(e.target.value) || 1),
                    }))
                  }
                />
                min
              </>
            }
          />
          {item.type === "video" || bodyParts.some((part) => part.type === "video") ? (
            <ContinueRow
              checked={continueSettings.requireWatch}
              disabled={readOnly || saving}
              onChange={(on) => setContinueSettings((prev) => ({ ...prev, requireWatch: on }))}
              title="Watch the video"
              subtext={
                continueSettings.requireWatch &&
                embedVideoParts(bodyParts).length > 0 &&
                lockableVideoParts(bodyParts).length === 0
                  ? "YouTube and Vimeo cannot be locked. Upload a file if they must watch without skipping."
                  : "They cannot skip ahead. Complete waits until an uploaded video is watched to the end."
              }
            />
          ) : null}
          {hasQuestions ? (
            <>
              <ContinueRow
                checked={requireKnowledge}
                disabled
                title="Submit questions"
                subtext={
                  requireKnowledge
                    ? "Required. Turn off Require Knowledge in Activity settings to make this optional."
                    : "Optional. Require Knowledge is off in Activity settings."
                }
              />
              {requireKnowledge ? (
              <ContinueRow
                checked={continueSettings.requirePass}
                disabled={
                  readOnly ||
                  saving ||
                  questions.some((q) => q.scoring?.answerMode === "manual")
                }
                onChange={(on) => setContinueSettings((prev) => ({ ...prev, requirePass: on }))}
                title="Minimum score"
                subtext={
                  questions.some((q) => q.scoring?.answerMode === "manual")
                    ? "Unavailable while any question is scored by hand."
                    : continueSettings.requirePass
                      ? <span className="al-cb-continue-row-sub--warn">{passAttemptsWarn}</span>
                      : "Only when every question can be auto-scored."
                }
                extra={
                  <>
                    <input
                      type="number"
                      min={0}
                      max={100}
                      className="al-cb-input al-cb-input--inline"
                      value={continueSettings.minPercent}
                      disabled={readOnly || saving || !continueSettings.requirePass}
                      aria-label="Minimum percent to continue"
                      onChange={(e) =>
                        setContinueSettings((prev) => ({
                          ...prev,
                          minPercent: Math.min(100, Math.max(0, Number(e.target.value) || 0)),
                        }))
                      }
                    />
                    %
                  </>
                }
              />
              ) : null}
            </>
          ) : null}
          {anatomy.special === "discussion" ? (
            <>
              <p className="al-cb-continue-group">Discussion</p>
              <ContinueRow
                checked={continueSettings.discussion.replyToPrompt}
                disabled={readOnly || saving || !board.allowPromptResponse}
                onChange={(on) =>
                  setContinueSettings((prev) => ({
                    ...prev,
                    discussion: { ...prev.discussion, replyToPrompt: on },
                  }))
                }
                title="Reply to the prompt"
                subtext={
                  board.allowPromptResponse
                    ? "One response to the discussion prompt."
                    : "Turn on prompt replies in Activity settings first."
                }
              />
              <ContinueRow
                checked={continueSettings.discussion.replyToOthers > 0}
                disabled={readOnly || saving || !board.allowReplyToOthers}
                onChange={(on) =>
                  setContinueSettings((prev) => ({
                    ...prev,
                    discussion: {
                      ...prev.discussion,
                      replyToOthers: on ? Math.max(1, prev.discussion.replyToOthers || 1) : 0,
                    },
                  }))
                }
                title="Reply to others"
                subtext={
                  board.allowReplyToOthers
                    ? "Classmate replies. Their own post counts if the board is still empty."
                    : "Turn on replies to others in Activity settings first."
                }
                extra={
                  <>
                    <input
                      type="number"
                      min={1}
                      className="al-cb-input al-cb-input--inline"
                      value={continueSettings.discussion.replyToOthers || 1}
                      disabled={readOnly || saving || continueSettings.discussion.replyToOthers <= 0}
                      aria-label="Other responses required"
                      onChange={(e) =>
                        setContinueSettings((prev) => ({
                          ...prev,
                          discussion: {
                            ...prev.discussion,
                            replyToOthers: Math.max(1, Number(e.target.value) || 1),
                          },
                        }))
                      }
                    />
                    {continueSettings.discussion.replyToOthers === 1 ? "reply" : "replies"}
                  </>
                }
              />
            </>
          ) : null}
        </div>
        <p className="al-cb-style-hint">
          Leaves when the learner
          {continuePrerequisites(continueSettings, { hasQuestions, requireKnowledge }).includes("questions")
            ? " submits every question,"
            : ""}
          {continuePrerequisites(continueSettings, { hasQuestions, requireKnowledge }).includes("discussion")
            ? " meets the discussion replies,"
            : ""}
          {continueSettings.minDwellMinutes > 0
            ? ` waits ${continueSettings.minDwellMinutes} minute${continueSettings.minDwellMinutes === 1 ? "" : "s"},`
            : ""}
          {continueSettings.requireWatch ? " watches the video to the end," : ""}
          {continueSettings.requirePass ? ` scores at least ${continueSettings.minPercent}%,` : ""}
          {" "}then clicks Complete.
        </p>
        <div className="al-cb-settings-actions">
          <button type="button" className="al-cb-btn" onClick={() => setContinueOpen(false)}>
            Done
          </button>
        </div>
      </Dialog>
    ) : null}

    {submissionOpen ? (
      <Dialog
        open
        title="Submission settings"
        subtitle="What they see after they turn the questions in. This is not the continue gate — Complete, dwell, and a pass mark stay under Continue settings."
        width="32rem"
        onClose={() => setSubmissionOpen(false)}
      >
        <p className="al-cb-style-hint">
          Submit locks the attempt. Right/wrong and the answer key are off
          unless you turn them on. Points are separate — a score can hint at
          a miss without naming it.
        </p>
        <div className="al-cb-continue-list" data-tour="cb-submission-panel">
          <ContinueRow
            checked={submissionSettings.showIncorrect}
            disabled={readOnly || saving}
            onChange={(on) => setSubmissionSettings((prev) => ({ ...prev, showIncorrect: on }))}
            title="Show which answers were right or wrong"
            subtext="Marks each question after they submit, so they can compare tries without reading a score."
          />
          <ContinueRow
            checked={submissionSettings.showAnswerKey}
            disabled={readOnly || saving}
            onChange={(on) => setSubmissionSettings((prev) => ({ ...prev, showAnswerKey: on }))}
            title="Show the answer key"
            subtext="Shows the correct answer on every question after they submit."
          />
          <ContinueRow
            checked={submissionSettings.showTotalPoints}
            disabled={readOnly || saving}
            onChange={(on) => setSubmissionSettings((prev) => ({ ...prev, showTotalPoints: on }))}
            title="Show total points after submit"
            subtext="The score banner. If anything is still marked by hand, the total is only what is scored so far and we say how many points are still with you."
          />
          <ContinueRow
            checked={submissionSettings.showQuestionPoints}
            disabled={readOnly || saving}
            onChange={(on) => setSubmissionSettings((prev) => ({ ...prev, showQuestionPoints: on }))}
            title="Show points per question"
            subtext="2/2, 1/2 for attempt credit, or 0/2. That can give away a miss, but it is not as straightforward as Incorrect or the key — they see a score, not the answer. Unmarked questions say they are waiting for you, not 0."
          />
        </div>
        <div className="al-cb-settings-actions">
          <button type="button" className="al-cb-btn" onClick={() => setSubmissionOpen(false)}>
            Done
          </button>
        </div>
      </Dialog>
    ) : null}

    {settingsOpen ? (
      <Dialog
        open
        title="Activity settings"
        subtitle="Turn fields on or off. Minutes on the form are hour credit for completing the step. Points, when this activity has no questions, are set on the form."
        width="28rem"
        onClose={() => setSettingsOpen(false)}
      >
        <p className="al-cb-style-hint">
          Choose what this activity uses. Values are edited on the main form.
        </p>
        <div className="al-cb-settings-stack">
          <div data-tour="cb-settings-includes">
            {INCLUDE_SETTING_KEYS.map((key) => (
              <label key={key} className="al-cb-check">
                <input
                  type="checkbox"
                  checked={includes[key]}
                  disabled={readOnly || saving}
                  onChange={(e) => setInclude(key, e.target.checked)}
                />
                Include {INCLUDE_SETTING_META[key].label.toLowerCase()}
              </label>
            ))}
          </div>
          <div data-tour="cb-settings-questions">
            <label
              className="al-cb-check"
              title="Add a question set to this activity. Off by default on readings, videos, and similar."
            >
              <input
                type="checkbox"
                checked={hasQuestions}
                disabled={readOnly || saving}
                onChange={(e) => {
                  const on = e.target.checked;
                  setHasQuestions(on);
                  setContinueSettings((prev) =>
                    normalizeContinueSettings(prev, item.type, { hasQuestions: on, requireKnowledge }),
                  );
                }}
              />
              Include questions
            </label>
            <label
              className="al-cb-check"
              title="Students must finish the knowledge check before they can complete this activity. On by default."
            >
              <input
                type="checkbox"
                checked={requireKnowledge}
                disabled={readOnly || saving}
                onChange={(e) => {
                  const on = e.target.checked;
                  setRequireKnowledge(on);
                  setContinueSettings((prev) =>
                    normalizeContinueSettings(prev, item.type, { hasQuestions, requireKnowledge: on }),
                  );
                }}
              />
              Require Knowledge
            </label>
          </div>
          <label
            className="al-cb-check"
            data-tour="cb-settings-points"
            title={
              hasQuestions
                ? "Points come from the questions. Awarding is set on each question, not here."
                : anatomy.special === "discussion"
                  ? "Discussions stay in the grade weights. Include points stays on until that changes. How the points are awarded is on the form."
                  : "Grade this activity out of a set number of points. On the form, award all or a set number on completion, or mark the work by hand."
            }
          >
            <input
              type="checkbox"
              checked={hasQuestions || includePoints || anatomy.special === "discussion"}
              disabled={readOnly || saving || hasQuestions || anatomy.special === "discussion"}
              onChange={(e) => {
                const on = e.target.checked;
                setIncludePoints(on);
                if (on && !assignmentPoints) setAssignmentPoints(10);
              }}
            />
            Include points
          </label>
          {anatomy.special === "discussion" ? (
            <div data-tour="cb-settings-board">
              <hr className="al-cb-settings-rule" />
              {DISCUSSION_BOARD_KEYS.map((key) => (
                <label key={key} className="al-cb-check">
                  <input
                    type="checkbox"
                    checked={board[key]}
                    disabled={readOnly || saving}
                    onChange={(e) => setBoard((prev) => ({ ...prev, [key]: e.target.checked }))}
                  />
                  {DISCUSSION_BOARD_META[key].label}
                </label>
              ))}
            </div>
          ) : null}
          <hr className="al-cb-settings-rule" />
          <div data-tour="cb-settings-gates">
            {renderSetting("required", readOnly || saving)}
            {renderSetting("requirePrevious", readOnly || saving)}
          </div>
          <hr className="al-cb-settings-rule" />
          <div className="al-cb-settings-chat" data-tour="cb-settings-chat">
            <label className="al-cb-check">
              <input
                type="checkbox"
                checked={studentMayMessage}
                disabled={readOnly || saving}
                onChange={(e) => setStudentMayMessage(e.currentTarget.checked)}
              />
              Student + Teacher Chat
            </label>
            <p className="al-cb-settings-chat-hint">Student may send messages in the chat to the teacher. They can always read it.</p>
          </div>
        </div>
        <div className="al-cb-settings-actions">
          <button type="button" className="al-cb-btn" onClick={() => setSettingsOpen(false)}>
            Done
          </button>
        </div>
      </Dialog>
    ) : null}

    {hint === "minutes" ? (
      <HintDialog title="Estimated minutes" onClose={() => setHint(null)}>
        <p>
          Hour credit for doing this step — how long it should take toward the course’s
          seat-time total. It is not a score. Completing a reading still counts these
          minutes even when there are no points.
        </p>
        <p>
          To give a grade, turn on Include points, or add a question. With no questions,
          the form awards all or a set number on completion, or leaves the score for you to mark.
          A question takes the grade over. Free vs keyed vs manual is set on each question.
        </p>
      </HintDialog>
    ) : null}
    {hint === "points" ? (
      <HintDialog title="Question points" onClose={() => setHint(null)}>
        <p>
          This activity’s points are the sum of the questions. They exist so scored work
          can count in the learner’s overall course grade (the grade rubric on course details).
        </p>
        <p>
          Minutes stay hour credit for completing the step. These question points replace
          any completion award on the activity. How each question is marked (free, keyed, or
          manual) is in that question’s answer settings.
        </p>
      </HintDialog>
    ) : null}

    <NotificationModal
      isOpen={discardOpen}
      type="warning"
      icon={<TriangleAlert />}
      title="Discard unsaved changes?"
      message="Edits to this item will be lost."
      overlay
      overlayClose
      showOkButton={false}
      align="center"
      iconSize="2.25rem"
      titleSize="1.05rem"
      zIndex={1300}
      style={COURSE_DIALOG_STYLE}
      onClose={() => setDiscardOpen(false)}
    >
      <div className="al-courses-assign-actions">
        <button
          type="button"
          className="al-cb-btn al-cb-btn--ghost"
          onClick={() => setDiscardOpen(false)}
        >
          Keep editing
        </button>
        <button
          type="button"
          className="al-cb-btn al-cb-btn--danger"
          onClick={() => {
            setDiscardOpen(false);
            finishClose();
          }}
        >
          Discard
        </button>
        </div>
      </NotificationModal>
      <NotificationModal
        isOpen={revertOpen}
        type="warning"
        icon={<TriangleAlert />}
        title="Revert to Draft?"
        message="This item is incomplete and cannot stay published. Saving will revert it to Draft so students will not see it."
        overlay
        overlayClose
        showOkButton={false}
        align="center"
        iconSize="2.25rem"
        titleSize="1.05rem"
        zIndex={1300}
        style={COURSE_DIALOG_STYLE}
        onClose={() => setRevertOpen(false)}
      >
        <div className="al-courses-assign-actions">
          <button
            type="button"
            className="al-cb-btn al-cb-btn--ghost"
            onClick={() => setRevertOpen(false)}
          >
            Keep editing
          </button>
          <button
            type="button"
            className="al-cb-btn"
            disabled={saving}
            onClick={() => {
              setRevertOpen(false);
              void save({ allowRevert: true });
            }}
          >
            Revert and save
          </button>
        </div>
      </NotificationModal>
    </>
  );
}


function NamePromptDialog({
  prompt,
  onClose,
}: {
  prompt: {
    title: string;
    label: string;
    placeholder: string;
    onSubmit: (name: string) => void;
  } | null;
  onClose: () => void;
}) {
  const [name, setName] = useState("");

  // Remount per prompt so the field never carries the previous name over.
  useEffect(() => {
    if (prompt) setName("");
  }, [prompt]);

  const submit = () => {
    if (!prompt) return;
    prompt.onSubmit(name.trim());
    onClose();
  };

  return (
    <NotificationModal
      isOpen={Boolean(prompt)}
      type="neutral"
      icon={<Plus />}
      title={prompt?.title ?? "Add"}
      message={null}
      overlay
      overlayClose
      showOkButton={false}
      align="center"
      iconSize="2.25rem"
      titleSize="1.05rem"
      style={COURSE_DIALOG_STYLE}
      onClose={onClose}
    >
      <form
        className="al-courses-dialog-form"
        data-tour="cb-name-prompt"
        onSubmit={(event) => {
          event.preventDefault();
          submit();
        }}
      >
        <label className="al-cb-label" htmlFor="al-cb-name-prompt">
          {prompt?.label}
        </label>
        <input
          id="al-cb-name-prompt"
          className="al-cb-input"
          value={name}
          placeholder={prompt?.placeholder}
          autoFocus
          onChange={(e) => setName(e.target.value)}
        />
        <p className="al-cb-count">You can rename it later.</p>
        <div className="al-courses-assign-actions">
          <button type="button" className="al-cb-btn al-cb-btn--ghost" onClick={onClose}>
            Cancel
          </button>
          <button type="submit" className="al-cb-btn">
            Create
          </button>
        </div>
      </form>
    </NotificationModal>
  );
}


// ---------------------------------------------------------------------------
// Body parts — one compact stack. Icon tabs add html / image / video / audio / file.
// Media and documents do not go in the rich-text part; that is stripped on paste too.
// ---------------------------------------------------------------------------

type MediaKind = "image" | "video" | "audio";

type MediaDraft = {
  url: string;
  mediaId?: string;
  kind?: MediaKind;
} & MediaSize;

function MediaSizeFields({
  width,
  height,
  lockScale,
  disabled,
  natural,
  fallbackRatio,
  onChange,
}: {
  width?: number;
  height?: number;
  lockScale?: boolean;
  disabled: boolean;
  natural: { w: number; h: number } | null;
  fallbackRatio?: number | null;
  onChange: (next: MediaSize) => void;
}) {
  const locked = lockScale !== false;
  const ratio =
    natural && natural.w > 0 && natural.h > 0
      ? natural.w / natural.h
      : width && height
        ? width / height
        : fallbackRatio || null;

  const emit = (next: MediaSize) => {
    onChange(mediaSizeFrom({ ...next, lockScale: next.lockScale === false ? false : undefined }));
  };

  const setWidth = (raw: string) => {
    if (!raw.trim()) {
      emit({ width: undefined, height: locked ? undefined : height, lockScale });
      return;
    }
    const next = sanitizeMediaPx(raw);
    if (!next) return;
    if (locked && ratio) emit({ width: next, height: Math.max(1, Math.round(next / ratio)), lockScale });
    else emit({ width: next, height, lockScale });
  };

  const setHeight = (raw: string) => {
    if (!raw.trim()) {
      emit({ height: undefined, width: locked ? undefined : width, lockScale });
      return;
    }
    const next = sanitizeMediaPx(raw);
    if (!next) return;
    if (locked && ratio) emit({ width: Math.max(1, Math.round(next * ratio)), height: next, lockScale });
    else emit({ width, height: next, lockScale });
  };

  const toggleLock = () => {
    const nextLock = !locked;
    if (!nextLock) {
      emit({ width, height, lockScale: false });
      return;
    }
    if (width && ratio) emit({ width, height: Math.max(1, Math.round(width / ratio)), lockScale: true });
    else if (height && ratio) emit({ width: Math.max(1, Math.round(height * ratio)), height, lockScale: true });
    else emit({ width, height, lockScale: true });
  };

  return (
    <div className="al-cb-media-size">
      <label className="al-cb-media-size-field">
        <span>W</span>
        <input
          className="al-cb-input"
          type="number"
          min={1}
          max={4000}
          inputMode="numeric"
          placeholder={natural ? String(natural.w) : "auto"}
          value={width ?? ""}
          disabled={disabled}
          onChange={(e) => setWidth(e.target.value)}
        />
      </label>
      <button
        type="button"
        className={`al-cb-icon-btn${locked ? " al-cb-icon-btn--on" : ""}`}
        title={locked ? "Unlock scale" : "Lock scale"}
        aria-label={locked ? "Scale is locked" : "Scale is unlocked"}
        aria-pressed={locked}
        disabled={disabled}
        onClick={toggleLock}
      >
        {locked ? <Lock size={12} /> : <Unlock size={12} />}
      </button>
      <label className="al-cb-media-size-field">
        <span>H</span>
        <input
          className="al-cb-input"
          type="number"
          min={1}
          max={4000}
          inputMode="numeric"
          placeholder={natural ? String(natural.h) : "auto"}
          value={height ?? ""}
          disabled={disabled}
          onChange={(e) => setHeight(e.target.value)}
        />
      </label>
      <span className="al-cb-media-size-unit">px</span>
    </div>
  );
}

function MediaPartField({
  kind,
  url,
  mediaId,
  width,
  height,
  lockScale,
  disabled,
  onChange,
}: {
  kind: MediaKind;
  url: string;
  mediaId?: string;
  width?: number;
  height?: number;
  lockScale?: boolean;
  disabled: boolean;
  onChange: (next: MediaDraft) => void;
}) {
  const { notice } = useNotification();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [linkOpen, setLinkOpen] = useState(!mediaId && Boolean(url));
  const [pickerOpen, setPickerOpen] = useState(false);
  const [pickerKind, setPickerKind] = useState<MediaKind>(kind);
  const [files, setFiles] = useState<LearnMedia[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [broken, setBroken] = useState(false);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);

  useEffect(() => {
    setBroken(false);
    setNatural(null);
  }, [url, kind]);

  const playback = kind === "video" ? videoPlayback(url) : null;
  const emptyLabel = kind === "image" ? "No image yet" : kind === "audio" ? "No audio yet" : "No video yet";

  const size = mediaSizeFrom({ width, height, lockScale });
  const pickerFiles = files?.filter((file) => file.kind === pickerKind) ?? null;

  const apply = (row: LearnMedia) => {
    const nextKind: MediaKind = row.kind === "video" || row.kind === "audio" ? row.kind : "image";
    onChange({ url: row.url, mediaId: row.id, kind: nextKind });
    setLinkOpen(false);
    setPickerOpen(false);
  };

  const openPicker = async () => {
    if (disabled || busy) return;
    setPickerKind(kind);
    setPickerOpen(true);
    setFiles(null);
    try {
      const [images, videos, audios] = await Promise.all([
        listMyMedia("image"),
        listMyMedia("video"),
        listMyMedia("audio"),
      ]);
      setFiles([...images, ...videos, ...audios]);
    } catch (err) {
      notice({ type: "error", title: "Could not load your files", subtext: errorMessage(err), duration: 7000 });
      setPickerOpen(false);
    }
  };

  const onUpload = async (file: File | undefined) => {
    if (!file || disabled) return;
    setBusy(true);
    try {
      const row = await uploadLearnMedia(file, kind);
      setFiles((prev) => (prev ? [row, ...prev.filter((f) => f.id !== row.id)] : [row]));
      apply(row);
    } catch (err) {
      notice({ type: "error", title: "Could not upload the file", subtext: errorMessage(err), duration: 7000 });
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  let frame: ReactNode;
  if (!url) {
    const EmptyIcon = kind === "image" ? ImageIcon : kind === "audio" ? Headphones : Video;
    frame = (
      <button
        type="button"
        className="al-cb-media-empty"
        disabled={disabled || busy}
        onClick={() => fileRef.current?.click()}
      >
        <EmptyIcon size={22} strokeWidth={1.6} />
        <span>{emptyLabel}</span>
        <span className="al-cb-media-empty-hint">Upload, or use the buttons on the left</span>
      </button>
    );
  } else if (kind === "image") {
    frame = broken ? (
      <p className="al-cb-media-empty-hint">{emptyLabel} — that link did not load.</p>
    ) : (
      <img
        src={url}
        alt=""
        style={mediaBoxStyle(size)}
        onError={() => setBroken(true)}
        onLoad={(e) => {
          const img = e.currentTarget;
          if (img.naturalWidth && img.naturalHeight) {
            setNatural({ w: img.naturalWidth, h: img.naturalHeight });
          }
        }}
      />
    );
  } else if (kind === "audio") {
    frame = (
      <audio className="al-cb-media-audio" src={url} controls preload="metadata" />
    );
  } else if (!playback) {
    frame = <p className="al-cb-media-empty-hint">Enter a video file, YouTube, or Vimeo URL.</p>;
  } else if (playback.kind === "file") {
    frame = (
      <video
        className="al-cb-media-video"
        src={playback.src}
        controls
        preload="metadata"
        style={mediaBoxStyle(size, { defaultRatio: "16 / 9" })}
        onLoadedMetadata={(e) => {
          const video = e.currentTarget;
          if (video.videoWidth && video.videoHeight) {
            setNatural({ w: video.videoWidth, h: video.videoHeight });
          }
        }}
      />
    );
  } else {
    frame = (
      <div className="al-cb-media-embed" style={mediaBoxStyle(size, { defaultRatio: "16 / 9" })}>
        <iframe
          src={playback.src}
          title={kind === "video" ? "Video preview" : "Media"}
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
        />
      </div>
    );
  }

  return (
    <div className="al-cb-media">
      <div className="al-cb-media-card">
        <div className="al-cb-media-sources">
          <button
            type="button"
            className={`al-cb-icon-btn${linkOpen ? " al-cb-icon-btn--on" : ""}`}
            title="Link"
            aria-label="Use a link"
            disabled={disabled || busy}
            onClick={() => setLinkOpen((open) => !open)}
          >
            <Link2 size={13} />
          </button>
          <button
            type="button"
            className="al-cb-icon-btn"
            title="Upload"
            aria-label="Upload a file"
            disabled={disabled || busy}
            onClick={() => fileRef.current?.click()}
          >
            {busy ? <Loader2 size={13} className="al-cb-spin" /> : <Upload size={13} />}
          </button>
          <button
            type="button"
            className={`al-cb-icon-btn${pickerOpen ? " al-cb-icon-btn--on" : ""}`}
            title="My files"
            aria-label="Choose from my files"
            disabled={disabled || busy}
            onClick={() => void openPicker()}
          >
            <FolderOpen size={13} />
          </button>
        </div>
        <div className={`al-cb-media-frame${url && !broken ? " al-cb-media-frame--filled" : ""}`}>
          {frame}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept={kind === "image" ? "image/*" : kind === "audio" ? "audio/*" : "video/*"}
          hidden
          disabled={disabled || busy}
          onChange={(e) => void onUpload(e.target.files?.[0])}
        />
      </div>
      {linkOpen ? (
        <input
          className="al-cb-input"
          type="url"
          placeholder={
            kind === "image"
              ? "Image URL https://…"
              : kind === "audio"
                ? "Audio URL https://…"
                : "Video URL, YouTube, or Vimeo"
          }
          value={url}
          disabled={disabled || busy}
          onChange={(e) => onChange({ url: e.target.value, mediaId, ...size })}
        />
      ) : null}
      {url && kind !== "audio" ? (
        <MediaSizeFields
          width={width}
          height={height}
          lockScale={lockScale}
          disabled={disabled || busy}
          natural={natural}
          fallbackRatio={kind === "video" ? 16 / 9 : null}
          onChange={(next) => onChange({ url, mediaId, ...next })}
        />
      ) : null}
      {pickerOpen ? (
        <Dialog
          open
          title="My files"
          subtitle="Images, videos, and audio you have uploaded"
          width="28rem"
          onClose={() => setPickerOpen(false)}
        >
          <div className="al-cb-media-pick-filters" role="tablist" aria-label="File type">
            <button
              type="button"
              role="tab"
              aria-selected={pickerKind === "image"}
              className={`al-cb-media-pick-filter${pickerKind === "image" ? " al-cb-media-pick-filter--on" : ""}`}
              onClick={() => setPickerKind("image")}
            >
              <ImageIcon size={13} />
              Images
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={pickerKind === "video"}
              className={`al-cb-media-pick-filter${pickerKind === "video" ? " al-cb-media-pick-filter--on" : ""}`}
              onClick={() => setPickerKind("video")}
            >
              <Video size={13} />
              Videos
            </button>
            <button
              type="button"
              role="tab"
              aria-selected={pickerKind === "audio"}
              className={`al-cb-media-pick-filter${pickerKind === "audio" ? " al-cb-media-pick-filter--on" : ""}`}
              onClick={() => setPickerKind("audio")}
            >
              <Headphones size={13} />
              Audio
            </button>
          </div>
          {pickerFiles == null ? (
            <p className="al-cb-muted">Loading…</p>
          ) : pickerFiles.length === 0 ? (
            <p className="al-cb-muted">
              {pickerKind === "image"
                ? "No images yet. Upload one first."
                : pickerKind === "audio"
                  ? "No audio yet. Upload one first."
                  : "No videos yet. Upload one first."}
            </p>
          ) : (
            <div className="al-cb-media-pick">
              {pickerFiles.map((file) => (
                <button
                  key={file.id}
                  type="button"
                  className={`al-cb-media-pick-row${file.id === mediaId ? " al-cb-media-pick-row--on" : ""}`}
                  onClick={() => apply(file)}
                >
                  {file.kind === "image" ? (
                    <img className="al-cb-media-pick-thumb" src={file.url} alt="" />
                  ) : file.kind === "audio" ? (
                    <span className="al-cb-media-pick-thumb al-cb-media-pick-thumb--icon">
                      <Headphones size={16} />
                    </span>
                  ) : (
                    <video className="al-cb-media-pick-thumb" src={file.url} muted preload="metadata" />
                  )}
                  <span className="al-cb-media-pick-name">{file.originalFilename || file.url}</span>
                  <span className="al-cb-media-pick-meta">
                    {new Date(file.createdAt).toLocaleDateString()}
                  </span>
                </button>
              ))}
            </div>
          )}
        </Dialog>
      ) : null}
    </div>
  );
}

const FILE_ACCEPT =
  ".pdf,.doc,.docx,.ppt,.pptx,.txt,.md,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,text/plain,text/markdown";

function FilePartField({
  url,
  name,
  mime,
  mediaId,
  disabled,
  onChange,
}: {
  url: string;
  name?: string;
  mime?: string;
  mediaId?: string;
  disabled: boolean;
  onChange: (next: { url: string; name: string; mime?: string; mediaId?: string }) => void;
}) {
  const { notice } = useNotification();
  const fileRef = useRef<HTMLInputElement | null>(null);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [files, setFiles] = useState<LearnMedia[] | null>(null);
  const [busy, setBusy] = useState(false);

  const apply = (row: LearnMedia) => {
    onChange({
      url: row.url,
      name: row.originalFilename || "File",
      mime: row.mimeType,
      mediaId: row.id,
    });
    setPickerOpen(false);
  };

  const openPicker = async () => {
    if (disabled || busy) return;
    setPickerOpen(true);
    setFiles(null);
    try {
      setFiles(await listMyMedia("file"));
    } catch (err) {
      notice({ type: "error", title: "Could not load your files", subtext: errorMessage(err), duration: 7000 });
      setPickerOpen(false);
    }
  };

  const onUpload = async (file: File | undefined) => {
    if (!file || disabled) return;
    setBusy(true);
    try {
      const row = await uploadLearnMedia(file, "file");
      apply(row);
    } catch (err) {
      notice({ type: "error", title: "Could not upload the file", subtext: errorMessage(err), duration: 7000 });
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  return (
    <div className="al-cb-media">
      <div className="al-cb-media-card">
        <div className="al-cb-media-sources">
          <button
            type="button"
            className="al-cb-icon-btn"
            title="Upload"
            aria-label="Upload a file"
            disabled={disabled || busy}
            onClick={() => fileRef.current?.click()}
          >
            {busy ? <Loader2 size={13} className="al-cb-spin" /> : <Upload size={13} />}
          </button>
          <button
            type="button"
            className={`al-cb-icon-btn${pickerOpen ? " al-cb-icon-btn--on" : ""}`}
            title="My files"
            aria-label="Choose from my files"
            disabled={disabled || busy}
            onClick={() => void openPicker()}
          >
            <FolderOpen size={13} />
          </button>
        </div>
        <div className={`al-cb-media-frame${url ? " al-cb-media-frame--filled" : ""}`}>
          {url ? (
            <p className="al-cb-file-name" title={mime || name || "File"}>
              <Paperclip size={14} aria-hidden /> {name || "File"}
            </p>
          ) : (
            <button
              type="button"
              className="al-cb-media-empty"
              disabled={disabled || busy}
              onClick={() => fileRef.current?.click()}
            >
              <Paperclip size={22} strokeWidth={1.6} />
              <span>No file yet</span>
              <span className="al-cb-media-empty-hint">Upload a PDF, Word, or PowerPoint students can preview</span>
            </button>
          )}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept={FILE_ACCEPT}
          hidden
          disabled={disabled || busy}
          onChange={(e) => void onUpload(e.target.files?.[0])}
        />
      </div>
      {pickerOpen ? (
        <Dialog
          open
          title="My files"
          subtitle="Documents you have uploaded"
          width="28rem"
          onClose={() => setPickerOpen(false)}
        >
          {files == null ? (
            <p className="al-cb-muted">Loading…</p>
          ) : files.length === 0 ? (
            <p className="al-cb-muted">No documents yet. Upload one first.</p>
          ) : (
            <div className="al-cb-media-pick">
              {files.map((file) => (
                <button
                  key={file.id}
                  type="button"
                  className={`al-cb-media-pick-row${file.id === mediaId ? " al-cb-media-pick-row--on" : ""}`}
                  onClick={() => apply(file)}
                >
                  <span className="al-cb-media-pick-thumb al-cb-media-pick-thumb--icon">
                    <Paperclip size={16} />
                  </span>
                  <span className="al-cb-media-pick-name">{file.originalFilename || file.url}</span>
                  <span className="al-cb-media-pick-meta">
                    {new Date(file.createdAt).toLocaleDateString()}
                  </span>
                </button>
              ))}
            </div>
          )}
        </Dialog>
      ) : null}
    </div>
  );
}

const PART_TABS = [
  { type: "html" as const, label: "Add text", icon: Type },
  { type: "image" as const, label: "Add image", icon: ImageIcon },
  { type: "video" as const, label: "Add video", icon: Video },
  { type: "audio" as const, label: "Add audio", icon: Headphones },
  { type: "file" as const, label: "Add file", icon: Paperclip },
];

function BodyPartsEditor({
  label,
  placeholder,
  rows,
  parts,
  disabled,
  onChange,
  wordTarget,
}: {
  label: string;
  placeholder?: string;
  rows: number;
  parts: DraftPart[];
  disabled: boolean;
  onChange: (next: DraftPart[]) => void;
  wordTarget: { min: number; max: number | null } | null;
}) {
  const [dockAdd, setDockAdd] = useState(false);
  const headRef = useRef<HTMLDivElement | null>(null);
  const partsRef = useRef<HTMLDivElement | null>(null);
  const scrollToNewPart = useRef(false);

  useEffect(() => {
    const el = headRef.current;
    if (!el) return undefined;
    const root = el.closest(".al-dialog-body");
    const io = new IntersectionObserver(
      ([entry]) => setDockAdd(!entry.isIntersecting),
      { root: root instanceof Element ? root : null, threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const update = (index: number, patch: Partial<BodyPart>) => {
    onChange(
      parts.map((part, i) => {
        if (i !== index) return part;
        const next = { ...part, ...patch } as DraftPart;
        if ("mediaId" in patch && !patch.mediaId) delete (next as { mediaId?: string }).mediaId;
        if ("width" in patch && !patch.width) delete (next as { width?: number }).width;
        if ("height" in patch && !patch.height) delete (next as { height?: number }).height;
        if ("lockScale" in patch && patch.lockScale !== false) delete (next as { lockScale?: boolean }).lockScale;
        return next;
      }),
    );
  };
  const add = (type: BodyPart["type"]) => {
    scrollToNewPart.current = true;
    if (type === "html") onChange([...parts, { key: newLocalId(), type: "html", html: "" }]);
    else if (type === "image") onChange([...parts, { key: newLocalId(), type: "image", url: "", alt: "" }]);
    else if (type === "audio") onChange([...parts, { key: newLocalId(), type: "audio", url: "" }]);
    else if (type === "file") onChange([...parts, { key: newLocalId(), type: "file", url: "", name: "" }]);
    else onChange([...parts, { key: newLocalId(), type: "video", url: "" }]);
  };

  useEffect(() => {
    if (!scrollToNewPart.current) return undefined;
    scrollToNewPart.current = false;
    return afterPaint(() => {
      const scroller = partsRef.current?.closest(".al-dialog-body");
      if (!(scroller instanceof HTMLElement)) return;
      scroller.scrollTo({ top: scroller.scrollHeight, behavior: "smooth" });
    });
  }, [parts]);
  const move = (index: number, dir: -1 | 1) => {
    const next = [...parts];
    const swap = index + dir;
    if (swap < 0 || swap >= next.length) return;
    [next[index], next[swap]] = [next[swap], next[index]];
    onChange(next);
  };
  const htmlWords = countWords(
    htmlToPlain(parts.filter((p) => p.type === "html").map((p) => p.html).join(" ")),
  );
  const addBar = (docked: boolean) => (
    <div className={`al-cb-parts-add${docked ? " al-cb-parts-add--dock" : ""}`}>
      {PART_TABS.map((tab) => {
        const Icon = tab.icon;
        return (
          <button
            key={tab.type}
            type="button"
            className="al-cb-icon-btn"
            title={tab.label}
            aria-label={tab.label}
            disabled={disabled}
            onClick={() => add(tab.type)}
          >
            <Icon size={13} />
          </button>
        );
      })}
    </div>
  );

  return (
    <div className="al-cb-parts" ref={partsRef}>
      <div className="al-cb-parts-head" ref={headRef}>
        <ItemSectionTitle icon={Type}>{label}</ItemSectionTitle>
        {addBar(false)}
      </div>
      {parts.map((part, index) => (
        <div key={part.key} className="al-cb-part">
          <div className="al-cb-part-body">
            {part.type === "html" ? (
              <RichTextEditor
                value={part.html}
                minHeight={`${(index === 0 ? rows : 3) * 1.5}rem`}
                disabled={disabled}
                placeholder={index === 0 ? placeholder : "Write content…"}
                preview={false}
                onChange={(html) => update(index, { type: "html", html })}
              />
            ) : part.type === "file" ? (
              <FilePartField
                url={part.url}
                name={part.name}
                mime={part.mime}
                mediaId={part.mediaId}
                disabled={disabled}
                onChange={(next) =>
                  update(index, {
                    type: "file",
                    url: next.url,
                    name: next.name,
                    mime: next.mime,
                    mediaId: next.mediaId,
                  })
                }
              />
            ) : (
              <MediaPartField
                kind={part.type}
                url={part.url}
                mediaId={part.mediaId}
                width={"width" in part ? part.width : undefined}
                height={"height" in part ? part.height : undefined}
                lockScale={"lockScale" in part ? part.lockScale : undefined}
                disabled={disabled}
                onChange={(next) =>
                  update(index, {
                    type: next.kind ?? part.type,
                    url: next.url,
                    mediaId: next.mediaId,
                    width: next.width,
                    height: next.height,
                    lockScale: next.lockScale,
                  })
                }
              />
            )}
          </div>
          <div className="al-cb-part-tools">
            <button
              type="button"
              className="al-cb-icon-btn"
              aria-label="Move up"
              disabled={disabled || index === 0}
              onClick={() => move(index, -1)}
            >
              <ArrowUp size={11} />
            </button>
            <button
              type="button"
              className="al-cb-icon-btn"
              aria-label="Move down"
              disabled={disabled || index === parts.length - 1}
              onClick={() => move(index, 1)}
            >
              <ArrowDown size={11} />
            </button>
            <button
              type="button"
              className="al-cb-icon-btn al-cb-icon-btn--danger"
              aria-label="Remove part"
              disabled={disabled || (parts.length === 1 && part.type === "html")}
              onClick={() => onChange(parts.filter((_, i) => i !== index))}
            >
              <Trash2 size={11} />
            </button>
          </div>
        </div>
      ))}
      {dockAdd ? addBar(true) : null}
      {wordTarget ? (
        <p className="al-cb-count">
          {htmlWords} words · target {wordTarget.min}–{wordTarget.max}
        </p>
      ) : null}
    </div>
  );
}
